import { isParamTrue } from '../utils/url';
import {
  BAM_PLAYER_FIT,
  CC_LOCALES,
  CLOSED_CAPTIONS_MODE,
  HORIZONTAL_POSITION,
  PRODUCT_ACTION_TYPE,
  SCALE_MODE,
  FOCUS_MODE,
  NAVIGATION_MODE,
  VIEW_STATE,
} from './constants';
import {
  getDefaultPlayerSettings,
  getSettingsString,
  parsePlayerSettings,
} from './settings';
import { ChevronLeft, ChevronRight } from './svg';
import { getRowNavigationInsets, getRowNavigationState } from './rowNavigation';
import { getPageStyleOfTagsWithName } from './styles';
import { uuid as uuidv4 } from '../utils/browser';
import Bowser from 'bowser';
import { enterFullscreen, exitFullscreen, isFullscreen } from '../utils/fullscreen';
import { getDeepActiveElement, getUrl } from '../utils/helpers';
import { getLocale, matchCcLocale } from '../utils/localeExtraction';
import { evaluateRuleGroup } from '../utils/rules';
import PostMessageConduit from '../PostMessageConduit';
import { cachedFnCall } from '../utils/cachedFn';
import PageFacade, { MAX_INT } from '../utils/pageFacade';

const copyStyleFromEl = (el) => el?.getAttribute('style') || '';

const applyStyleToEl = (el, style) => el?.setAttribute('style', style || '');

const { fetch, setTimeout, clearTimeout } = window;

const DEFAULT_LIMIT = 120; // Also is the maximum number of players that can be rendered
const AUTOPLAY_CASCADE_PLAY_DURATION_MS = 4000; // Time to wait before starting the next player in the autoplay cascade
const UNLOAD_PLAYER_UI_DELAY_MS = 0; // Time to wait before unloading the player UI after it has been hidden

const debugMode = isParamTrue('bambuserVodDiscover');

// Drop empty-string / null / undefined values so an unset appearance
// override doesn't clobber a valid base/default when spread on top of it.
// Appearances persist unset fields as "" (e.g. playerOverlayTitleFontSize),
// and a blank override blanking out a real default poisons downstream CSS
// var() fallback chains (an empty custom property is "declared", so the
// `var(--x, fallback)` fallback never fires).
const omitBlankValues = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined),
);

const resolveAppearance = ({ appearanceGroup, presentationContext }) => {
  if (!appearanceGroup) return null;

  try {
    const appearances = appearanceGroup.appearances || [];
    const defaultAppearance = appearances.find(app => app.type === 'default') || {};
    const otherAppearances = appearances.filter(app => app.type !== 'default');
    const rankedAppearances = otherAppearances.map((appearance) => {
      const appearanceRuleSet = appearance?.ruleSet?.data || {};
      const appearancePlayerConfig = appearance?.data;
      const evaluationResult = evaluateRuleGroup({ ruleData: appearanceRuleSet, presentationContext });
      return {
        ...evaluationResult,
        playerConfig: appearancePlayerConfig,
        appearanceId: appearance.id,
      };
    })
      .filter(result => result.isMatch)
      .sort((a, b) => b.score - a.score);

    const compositePlayerConfig = {
      ...omitBlankValues(defaultAppearance.data),
      ...omitBlankValues(rankedAppearances[0]?.playerConfig),
    };

    return {
      playerConfig: compositePlayerConfig || {},
      appearanceGroupId: appearanceGroup.id || null,
      appearanceId: rankedAppearances[0]?.appearanceId || null,
    };
  } catch (err) {
    console.warn('Error evaluating appearance rules:', err);
    return null;
  }
};

const loadPlaylist = async ({ videosDataSource, eventIds, channelId, videoIds, orgId, query, containerId, offset, presentationContext = {}, appearanceGroupOverride }) => {
  let playlist = [];
  let playlistMetadata = [];
  let playerConfig = {};
  let distributionPageId = null;
  let distributionContainerId = null;
  let pageSize = 0;
  let total = 0;
  let page = 0;
  let playlistId = null;
  let appearanceGroupId = null;
  let appearanceId = null;
  let placementId = null;

  if (videosDataSource === 'shows' && Array.isArray(eventIds) && eventIds.length > 0) {
    playlist = eventIds;
  } else if (videosDataSource === 'vod' && Array.isArray(videoIds) && videoIds.length > 0) {
    playlist = videoIds;

    const appearanceGroup = appearanceGroupOverride;
    if (appearanceGroup) {
      const appearance = resolveAppearance({ appearanceGroup, presentationContext });
      if (appearance) {
        playerConfig = appearance.playerConfig;
        appearanceGroupId = appearance.appearanceGroupId;
        appearanceId = appearance.appearanceId;
      }
    }
  } else if (videosDataSource === 'shows' && channelId) {
    // Get content from a Channel
    const cacheKey = `channel-${channelId}`;
    const { playlists } = await cachedFnCall(cacheKey, () => fetch(`${process.env.WIDGETS_APPENGINE_BASE_URL}/channels/${channelId}`)
      .then((response) => response.json())
      .then((json) => json || {}));

    for (const { shows } of playlists) {
      if (!Array.isArray(shows)) continue;
      for (const { showId, isLive, isArchived, duration, isTestShow } of shows) {
        if (!showId || !(isLive || isArchived) || duration <= 0 || isTestShow) continue;
        if (playlist.includes(showId)) continue;
        playlist.push(showId);
      }
    }
  } else if (videosDataSource === 'vod' && orgId) {
    const { productReference: sku } = findPageProperties();

    // Check for query parameter type overrides for distribution
    const {
      bamPlacementPreview: requestedBamPlacementPreview,
      bamForceDistV2: forceDistV2 = null,
    } = getPageQueryParamsKeyValues();
    const isBamPlacementPreview = Boolean(requestedBamPlacementPreview);
    const {
      videoPlaylistId: requestedVideoPlaylistId,
      appearanceGroupId: requestedAppearanceGroupId,
      componentId: overrideDistributionForContainerId,
    } = parseQueryParameterOverrides(requestedBamPlacementPreview);

    let localDistributionOverrides = {};
    if (isBamPlacementPreview) {
      // If we expect some overriden distribution we have to signal the expected videoPlaylistId and
      // appearanceGroup we expect to use. And if the current override query parameter defines either
      // the videoPlaylistId or appearanceGroupId to be undefined (0), we have to respect that as well.
      // REMARK: For the time being let's signal to the backend that we expect no video playlist or
      // appearance group to be provided if the value for these overrides is "0".
      if (overrideDistributionForContainerId && overrideDistributionForContainerId === containerId) {
        // ... if override component id is defined and it matches the component id of this instance
        localDistributionOverrides = {
          videoPlaylistId: requestedVideoPlaylistId,
          appearanceGroupId: requestedAppearanceGroupId,
        };
      } else if (!overrideDistributionForContainerId) {
        // ... if no override component id is defined, we will override regardless of component id
        localDistributionOverrides = {
          videoPlaylistId: requestedVideoPlaylistId,
          appearanceGroupId: requestedAppearanceGroupId,
        };
      }
    }
    if (forceDistV2) {
      localDistributionOverrides['useDistributionV2'] = forceDistV2;
    }

    const url = getUrl();
    const title = document.title;
    const availableParams = {
      // Component defined data
      orgId,
      query,
      containerId,
      // Additional data from page
      url,
      title,
      ...(sku && { sku }),
      ...(offset && { offset }),
      // Query parameters which could control distribution results...
      ...localDistributionOverrides,
    };

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(availableParams)) {
      if (value) params.append(key, value);
    }

    const cacheKey = `org-${orgId}-${params.toString()}`;
    const { data, pagination } = await cachedFnCall(cacheKey, () => fetch(`${process.env.WIDGETS_APPENGINE_BASE_URL}/distribution${params ? `?${params.toString()}` : ''}`)
      .then((response) => response.json())
      .then((json) => json || {}));
    playlist = data?.playlist || [];
    playlistMetadata = data?.playlistMetadata || [];
    distributionPageId = data?.pageId;
    distributionContainerId = data?.id;
    offset = pagination?.offset;
    pageSize = pagination?.pageSize;
    total = pagination?.total;
    page = pagination?.page;

    playlistId = data?.videoPlaylistId;
    placementId = data?.placementId;
    const distributionAppearance = resolveAppearance({ appearanceGroup: data?.appearanceGroup, presentationContext });
    if (distributionAppearance) {
      playerConfig = {
        ...(data?.playerConfig || {}),
        ...(distributionAppearance.playerConfig),
      };
      appearanceGroupId = distributionAppearance.appearanceGroupId;
      appearanceId = distributionAppearance.appearanceId;
    } else {
      playerConfig = data?.playerConfig || {};
    }
  }

  return {
    playlist,
    playlistMetadata,
    playerConfig,
    distributionPageId,
    distributionContainerId,
    offset,
    pageSize,
    total,
    page,
    playlistId,
    appearanceGroupId,
    appearanceId,
    placementId,
  };
};

const findSuggestShopifyProperities = () => {
  const shopifyProductProps = {};
  const productIdHiddenInputRef = document.querySelector('input[name="product-id"][type="hidden"]');
  if (productIdHiddenInputRef?.value) {
    shopifyProductProps['productReference'] = productIdHiddenInputRef.value;
  }

  return shopifyProductProps;
};

const _filterSchemaEntries = (entry, acceptedTypes = ['product']) => {
  // Accept entries that are missing the @type property
  if (typeof entry === 'object' && typeof entry['@type'] !== 'string') {
    return true;
  }

  // Ignore any/all entries in an array of schema.org objects that aren't of "Product" type
  return typeof entry === 'object' &&
    typeof entry['@type'] === 'string' &&
    acceptedTypes.includes(entry['@type'].toLowerCase());
};

const productFilter = (entry) => _filterSchemaEntries(entry, ['product']);
const productOrProductGroupFilter = (entry) => _filterSchemaEntries(entry, ['product', 'productgroup']);

const findSuggestLdJsonProductProperities = () => {
  const ldJsonProductProps = {};
  const scriptElements = document.querySelectorAll('script[type="application/ld+json"]');

  for (const scriptElement of scriptElements) {
    // Iterate through the script elements to find the one containing the product schema
    // NOTE: The below is (more or less) a copy of the parsing code used in functions
    // JsonSchemaProductTemplateSuggestion.js.
    try {
      let parsedSchema = JSON.parse(scriptElement.textContent);

      // Support schema where the outermost thing is a @graph
      if (Array.isArray(parsedSchema?.['@graph'])) {
        // If the parsed schema is a @graph, we will use the first Product or ProductGroup in the graph.
        const graphSchema = parsedSchema['@graph'].filter(productOrProductGroupFilter)[0];
        if (graphSchema) {
          parsedSchema = graphSchema;
        }
      } else if (parsedSchema?.['@graph']?.['@type'] === 'Product') {
        // If the parsed schema is a @graph with a single Product, we will use that.
        parsedSchema = parsedSchema['@graph'];
      }

      // Support schema where the outermost thing is a ProductGroup
      let productGroupSchema;
      if (parsedSchema?.['@type'] === 'ProductGroup') {
        productGroupSchema = parsedSchema;
        // If the parsed schema is a ProductGroup, we will use the first Product in the group.
        parsedSchema = parsedSchema.hasVariant?.[0] || null;
      }

      const productSchema = (Array.isArray(parsedSchema) ? parsedSchema : [parsedSchema])
        .filter(productFilter)[0];

      if (productSchema) {
        if (productGroupSchema?.productGroupID) {
          // If we're dealing with a ProductGroup, we can use the productGroupID as the product reference.
          // This is a unique identifier for the group of products which should hopefully
          // be a better identifier than the sku of the first product in the group.
          // NOTE: Use "productReference" here because that's the internal property of ours that'll
          // take precedence over any other "sku" or "gtin" props that may exist on the object.
          // See enrichObjectWithExpectedProductPropsMapping().
          parsedSchema.productReference = productGroupSchema?.productGroupID;
        }
        for (const [key, value] of Object.entries(parsedSchema)) {
          if (!ldJsonProductProps[key]) ldJsonProductProps[key] = value;
        }

        if (enrichObjectWithExpectedProductPropsMapping(ldJsonProductProps).productReference) {
          // We found a product reference, no need to continue looking through other script elements
          // NOTE: This matches the behavior in JsonSchemaProductTemplateSuggestion.js (see _getRawProductSchema())
          break;
        }
      }
    } catch (error) {
      console.error('Error parsing JSON-LD', error);
    }
  }

  return ldJsonProductProps;
};

const findSuggestMicrodataProductProperities = () => {
  const microdataProductProps = {};
  const productMetaContainer = document.querySelector('[itemtype="http://schema.org/Product"]');

  if (productMetaContainer) {
    const itempropElements = productMetaContainer.querySelectorAll('[itemprop]');

    for (const itempropElement of itempropElements) {
      const propName = itempropElement.getAttribute('itemprop');
      const propValue = itempropElement.innerText;

      if (!microdataProductProps[propName]) microdataProductProps[propName] = propValue;
    }
  }

  return microdataProductProps;
};

const enrichObjectWithExpectedProductPropsMapping = (data) => {
  const productIdPropPriority = [
    'sku',
    'gtin',
    'gtin8',
    'gtin12',
    'gtin13',
    'gtin14',
    'productID',
  ];

  for (const prop of productIdPropPriority) {
    if (data.productReference) break;
    if (data[prop]) {
      data.productReference = data[prop];
      break;
    }
  }

  return data;
};

const findPageProperties = () => {
  const res = {
    productReference: null,
  };

  try {
    const dataPagePropertiesFinder = [
      () => enrichObjectWithExpectedProductPropsMapping(findSuggestLdJsonProductProperities()),
      () => findSuggestShopifyProperities(),
      () => enrichObjectWithExpectedProductPropsMapping(findSuggestMicrodataProductProperities()),
    ];

    for (const dataFinder of dataPagePropertiesFinder) {
      const data = dataFinder();
      // Fill the "res" object with values from the first data provider that has something
      for (const key of Object.keys(res)) {
        if (!res[key] && data[key]) {
          // This property is still empty in the result object: use the value from the data provider
          res[key] = data[key];
        }
      }
      // No need to continue looking for data in next data
      // finder if we found all data we're looking for
      if (Object.values(res).every(Boolean)) break;
    }
  } catch (error) {
    // nothing to do really
  }

  return res;
};

const scrollToElement = (container, element, ignoreGapMeasurement, forceScroll) => {
  if (container && element) {
    const playerWidthPx = parseFloat(window.getComputedStyle(element).getPropertyValue('width'));
    const paddingOffset = ignoreGapMeasurement ? 0 : Math.ceil(0.03 * playerWidthPx);
    const playerOverflowPx = playerWidthPx - (container.scrollLeft + container.offsetWidth - element.offsetLeft);

    // 1.6 is a magic number that is used to determine if the player is too wide for the visible container
    const scrollPosition = (container.offsetWidth < playerWidthPx * 1.6)
      ? element.offsetLeft - (container.offsetWidth - playerWidthPx) / 2
      : container.scrollLeft + playerOverflowPx - paddingOffset + (playerWidthPx / 4);
    const currentScrollPosition = container.scrollLeft;

    const shouldScrollForward = currentScrollPosition < scrollPosition;
    const shouldScrollBackward = element.offsetLeft < container.scrollLeft;

    // Scroll smoothly to the calculated position, only if we would scroll forwards (or instantly scroll back
    // the intended behavior is to jump to expected player)
    if (forceScroll || shouldScrollForward || shouldScrollBackward) {
      // Use scroll instead of scrollTo to avoid the issue where some clients have
      // Overridden the default scrollTo behavior, which can cause unexpected behavior
      // scrollTo() is electively the same as scroll().
      container.scroll({
        left: scrollPosition,
        behavior: forceScroll ? 'instant' : 'smooth',
      });
    }
  }
};

const scrollPlaylist = (container, players, direction) => {
  // If there is no container or players, do nothing
  if (!container || !players || players.length === 0) return;

  const containerScrollLeft = Math.floor(container.scrollLeft) + players[0].offsetLeft;
  const containerScrollWidth = container.scrollWidth;
  const containerWidth = container.offsetWidth;

  // Check if the container is scrollable
  if (containerScrollWidth <= containerWidth) return;

  // Determine the current visible player
  let currentPlayerIndex = 0;
  for (let i = 0; i < players.length; i++) {
    const playerLeft = players[i].offsetLeft;
    const playerRight = playerLeft + players[i].offsetWidth;
    const playerWidth = players[i].offsetWidth;
    currentPlayerIndex = i;

    // Some cases there could be 1 or 2 px difference between containerScrollLeft and playerLeft
    // In this case, we should consider the player as visible and break the loop
    if (containerScrollLeft - playerLeft < 5) {
      break;
    }

    // Check if the player is fully or partially visible
    if (playerLeft >= (containerScrollLeft - i) && playerRight <= containerScrollLeft + containerWidth) {
      break;
    }
    // Check if the player is visible but the container is too small to show the whole player
    // In this case, we should scroll to the next player.

    // When doing this, we should also consider the case where containerScrollLeft is between playerLeft and playerRight
    // For example if we are the third player and if the second playerWidth is bigger than the containerWidth (playerWidth > containerWidth === true)
    else if (playerWidth > containerWidth && containerScrollLeft >= playerLeft && containerScrollLeft <= playerRight) {
      break;
    }
  }

  let scrollAmount = 0;

  if (direction === 'right') {
    if (currentPlayerIndex < players.length - 1) {
      // Scroll to the next video on the right
      scrollAmount = players[currentPlayerIndex + 1].offsetLeft - containerScrollLeft;
    }
  } else if (direction === 'left') {
    if (currentPlayerIndex > 0) {
      // Scroll to the previous video on the left
      scrollAmount = players[currentPlayerIndex - 1].offsetLeft - containerScrollLeft;
    }
  }

  // Perform the scroll if there is a valid scroll amount
  if (scrollAmount !== 0) {
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }
};

const resetStyleProperty = (currentStyle, originalStyle, propertyName) => {
  if (currentStyle[propertyName]) {
    if (originalStyle[propertyName]) {
      currentStyle[propertyName] = originalStyle[propertyName];
    } else {
      currentStyle[propertyName] = '';
    }
  };
};

const throttle = (func, limit) => {
  let lastCall = 0;
  let timeout = null;
  let lastArgs;
  let lastContext;

  return function (...args) {
    const now = Date.now();
    lastArgs = args;
    lastContext = this;

    const remaining = limit - (now - lastCall);

    if (remaining <= 0) {
      // Immediate execution
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      lastCall = now;
      func.apply(lastContext, lastArgs);
    } else if (!timeout) {
      // Trailing edge
      timeout = setTimeout(() => {
        lastCall = Date.now();
        timeout = null;
        func.apply(lastContext, lastArgs);
      }, remaining);
    }
  };
};

const emitDebugMessage = (debugId, eventType, data) => {
  if (!debugMode) return;
  try {
    window.__bambuserDebugMessages = window.__bambuserDebugMessages || [];
    window.__bambuserDebugMessages.push({
      eventType,
      debugId,
      time: Date.now(),
      data,
    });
  } catch (e) {
    console.error(e);
  }
};

const componentIdentifiers = [
  'playlist-id', // the id of the bam-playlist component, is mapped to the containerId param in the distribution API (LEGACY)
  'component-id', // the id of the bam-bundle component, is mapped to the containerId param in the distribution API
];

const getPagePlacementMetadata = () => {
  if (window.__bamPagePlacementMetadata) return window.__bamPagePlacementMetadata;

  const metadata = {};
  try {
    const url = new URL(getUrl());
    // Include url without query parameters and hash as placement metadata
    metadata.url = `${url.origin}${url.pathname}`;
  } catch (e) {
    // Failed to set placement metadata
    console.warn('Failed to set placement metadata', { err: e });
  }

  return window.__bamPagePlacementMetadata = metadata;
};

const getPageQueryParamsKeyValues = () => {
  if (window.__bamPageQueryParamsKeyValues) return window.__bamPageQueryParamsKeyValues;

  const params = {};
  try {
    const url = new URL(window.location.href);
    for (const [key, value] of url.searchParams.entries()) {
      params[key] = value;
    }
  } catch (e) {
    // Failed to set query params
    console.warn('Failed to set query params', { err: e });
  }

  return window.__bamPageQueryParamsKeyValues = params;
};

const parseQueryParameterOverrides = (previewQueryParam) => {
  if (!previewQueryParam) return {};

  try {
    // First check if the param is just a plain JSON string
    const decodedParam = decodeURIComponent(previewQueryParam);
    return JSON.parse(decodedParam);
  } catch (e) {
    // Fail silently and try next method
  }

  // previewQueryParam was not a valid JSON string, test if it's a base64 encoded JSON string
  try {
    const base64Decoded = window.atob(previewQueryParam);
    return JSON.parse(base64Decoded);
  } catch (e) {
    // Failed to parse query parameter overrides
    console.warn('Failed to parse JSON string or base64 encoded query parameter overrides', { err: e });
  }

  return {};
};

/**
 * A custom element that bundles multiple bam-player elements into a group.
 *
 * The component will automatically load a playlist of shows from a channel or a list of event IDs.
 * It will then render a grid of bam-player elements, each representing a show in the playlist.
 * The user can click on a player to expand it and focus on it, or hover over it to start playing it.
 *
 * There are multiple display modes, both for the initial thing that is rendered on the embed page,
 * and for the expanded view which is shown when the user clicks on a player.
 */
export default class BambuserBundleComponent extends window.HTMLElement {
  // Changes in these attributes will trigger attributeChangedCallback()
  static observedAttributes = [
    'channel-id',
    'event-ids',
    'mode', // row (default) | grid | single | fab | hidden
    'focus-mode', // carousel (default) | single | playpause | none
    'autoplay', // hover (default) | cascade
    'limit', // [0, N] - max number of players to render
    'soft-limit', // [0, N] - max number of players to render in contracted mode, no load more button
    'video-ids', // comma-separated list of video ids
    'org-id', // the expected org id for the bam-bundle component
    'query', // the query to use to fetch videos for defined org
    'player-settings', // settings for the players
    'zoom-on-hover', // zoom players on hover (true | false)
    'player-fit', // player fit for the bundle (same-height | same-width | exact-size | fill-parent)
    'player-justify', // justify-content for the players in the container
    'cookie-domain', // defines the domain for tracking cookies
    'cookie-ttl-days', // defines the TTL for tracking cookies
    'product-action', // defines how the product click is handled (none|expand|expand-product|modal|drawer)
    'closed-captions', // defines if we show closed captions (off|original|en-US|fr-FR... etc; see CC_LOCALES in constants.js)
    'show-scrollbars', // show native scrollbars on the playlist row and product strip (true | false)
    'show-vertical-navigation-buttons', // show prev/next buttons on the expanded playlist in portrait (true | false)
    'show-row-navigation-buttons', // show prev/next buttons on an overflowing playlist row (true | false)
    'collect-metrics', // opt out of metrics collection when embedded on bambuser.com (internal use)
    ...componentIdentifiers,

    // Unobserved but still used, should be observed?
    //'landscape-in-portrait',
    //'show-poster-when-paused',
    // start-muted
    // start-focused
    // 'fullscreen', // true | false (default)
    // start-video-id // Configure which video to start at
    // navigation-mode // [FAB | FAB-manual | none (default)]

    // TODO?
    // 'captions', on | off
    // 'size', // size of bam-player
  ];

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    const browser = Bowser.getParser(window.navigator.userAgent);
    this._isDesktop = browser.getPlatformType(true) === 'desktop';

    this._presentationalContext = {
      deviceType: ['desktop', 'mobile', 'tablet'].includes(browser.getPlatformType(true)) ? browser.getPlatformType(true) : undefined,
      viewportWidth: window.innerWidth,
    };

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' || event.key === 'Backspace') {
        this._contract();
      }
    });

    // Listen for messages from iframe window to contract the player,
    // Without this, when the player iframe is in focus and the parent window can't listen for key events
    // In the player code we have a listener for the Escape key and post a message to the parent window
    this._onWindowMessage = (message) => {
      try {
        const { eventName, data = {} } = message;
        if (eventName === 'livecommerce:keyup' && ['Escape', 'Backspace'].includes(data?.key)) {
          this._contract();
        }
        
        if (eventName === 'bam-playlist:contract-in-subpage' && data.context) {
          if (!this._isPlaylistAbovePageFacade) {
            // We are not the playlist currently being shown above the surfbehind iframe
            // and should not react to the message
            return;
          }
          // When we have a PageFacade open and the user contracts a playlist inside the
          // surfbehind iframe, we need to replace the playlist in the parent page (that's
          // visible on top of the surfbehind iframe) with the playlist the user contracted.
          // Reset this component with the new attributes provided in the message.
          this._resetWithImportedContext(data.context);
        }
        if (eventName === 'bam-playlist:expand-in-subpage') {
          // A playlist in the surfbehind iframe was expanded. If we happen to be
          // in "miniplayer" mode we need to revert to the original mode to avoid
          // overlapping the expanded playlist inside the iframe.
          if (!this._isPlaylistAbovePageFacade) {
            // We are not the playlist currently being shown above the surfbehind iframe
            // and should not react to the message
            return;
          }
          if (this.__attr.mode !== 'fab' && this._attr.mode === 'fab') {
            this._attrOverrides = {};
            this._setup();
          }
        }
      } catch (_) {
        // Do nothing
      }
    };
    PostMessageConduit.registerGlobalListener(this._onWindowMessage);

    this._playerSettings = getDefaultPlayerSettings();
    this._remotePlayerConfig = {};
    this._themeId = null;
    this._playerVisibilityMap = {};
    this._isExpanded = false;
    this._isMuted = undefined;
    this._savedFocus = null;
    this._customizations = null;
    this._placementMetadata = getPagePlacementMetadata();

    if (debugMode) {
      this._debugId = uuidv4();
      emitDebugMessage(this._debugId, 'create', { type: 'bam-playlist', element: this, attributes: this._attr });
    }
  }

  /** Standard Web Component methods */

  connectedCallback() {
    if (this._hasConnected) return;
    this._hasConnected = true;
    this.dataset.status = 'loading'; // Set initial status in the data-status attribute
    this._setup();
  }

  disconnectedCallback() {
    this._updateStylesheet();

    if (this._onWindowMessage) {
      PostMessageConduit.unregisterGlobalListener(this._onWindowMessage);
      this._onWindowMessage = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._hasConnected) {
      // Do nothing if the component hasn't been connected to the DOM yet
      return;
    }

    if (oldValue === newValue) return;

    // The following attributes should trigger a full re-render
    if ([
      'channel-id',
      'event-ids',
      'focus-mode',
      'autoplay',
      'limit',
      'soft-limit',
      'video-ids',
      'org-id',
      'query',
      'player-fit',
      'product-action',
      'closed-captions',
      'collect-metrics',
      ...componentIdentifiers,
    ].includes(name)) {
      this._setup();
      return;
    }

    if (name === 'player-settings') {
      this._updatePlayersSettingsFromAttributes();
      this._applyPlayerSettingsUpdate();
      this._updateStylesheet();
    }

    if (['mode', 'show-vertical-navigation-buttons', 'show-row-navigation-buttons'].includes(name)) {
      // These only affect the shadow-DOM stylesheet — no player reload needed.
      this._updateStylesheet();
    }
  }

  /** Custom methods */

  get channelId() {
    return this.getAttribute('channel-id');
  }

  get eventIds() {
    return (this.getAttribute('event-ids') || '').split(',').map(it => it.trim()).filter(Boolean);
  }

  get videoIds() {
    return (this.getAttribute('video-ids') || '').split(',').map(it => it.trim()).filter(Boolean);
  }

  get orgId() {
    return this.getAttribute('org-id');
  }

  getRelevantContainerId(fallback) {
    for (const idAttr of componentIdentifiers) {
      const id = this.getAttribute(idAttr);
      if (id) return id;
    }

    return fallback;
  }

  get _videosDataSource() {
    const { eventIds, videoIds, channelId, orgId } = this;
    if (Array.isArray(eventIds) && eventIds.length > 0) {
      return 'shows';
    } else if (Array.isArray(videoIds) && videoIds.length > 0) {
      return 'vod';
    } else if (channelId) {
      return 'shows';
    } else if (orgId) {
      return 'vod';
    }
    return null;
  }

  get _attr() {
    if (this._attrOverrides) {
      return {
        ...this.__attr,
        ...this._attrOverrides,
      };
    }
    return this.__attr;
  }

  get __attr() {
    const { orgId, channelId, eventIds, videoIds, _videosDataSource: videosDataSource } = this;

    // ?bamAutoplayVideo=<videoId> deep-links into the extended player on a specific VOD.
    // Cached per-instance because __attr is read many times per render; claimed on a
    // window flag so only the first bam-playlist on the page reacts.
    if (this._bamAutoplayVideoId === undefined) {
      if (window.__bamAutoplayVideoConsumed) {
        this._bamAutoplayVideoId = null;
      } else {
        const fromQuery = getPageQueryParamsKeyValues().bamAutoplayVideo || null;
        if (fromQuery) window.__bamAutoplayVideoConsumed = true;
        this._bamAutoplayVideoId = fromQuery;
      }
    }
    const bamAutoplayVideoId = this._bamAutoplayVideoId;

    const remoteProvidedSoftLimit = parseInt(this._remotePlayerConfig?.softLimit, 10);
    const validRemoteProvidedSoftLimit = Number.isSafeInteger(remoteProvidedSoftLimit) && remoteProvidedSoftLimit > 0 ? remoteProvidedSoftLimit : null;
    const providedSoftLimit = parseInt(this.getAttribute('soft-limit'), 10);
    const validProvidedSoftLimit = Number.isSafeInteger(providedSoftLimit) && providedSoftLimit > 0 ? providedSoftLimit : null;
    const softLimit = validProvidedSoftLimit || validRemoteProvidedSoftLimit || Infinity;

    // Range of possible values is integer in [0, DEFAULT_LIMIT]
    const limit = (() => {
      if (softLimit && !this.getAttribute('limit')) return DEFAULT_LIMIT;
      const providedLimit = parseInt(this.getAttribute('limit'), 10);
      return Number.isSafeInteger(providedLimit)
        ? Math.max(0, Math.min(parseInt(providedLimit, 10), DEFAULT_LIMIT))
        : DEFAULT_LIMIT;
    })();

    const _autoIndexId = this.autoIndexId;
    const autoIndexId = _autoIndexId >= 0 ? `playlist-auto-id-${_autoIndexId}` : null;

    const { productCardMode, title, overlayTextWrap } = this._remotePlayerConfig || {};

    // showScrollbars merges the HTML attribute on <bam-playlist> with the
    // resolved appearance config so a host page setting the attribute reaches
    // children even without an AppearanceGroup; passed through the settings
    // string so each <bam-player> can react like it does to title/overlayTextWrap.
    const showScrollbarsAttr = this.getAttribute('show-scrollbars');
    const showScrollbars = showScrollbarsAttr !== null
      ? showScrollbarsAttr === 'true'
      : this._remotePlayerConfig?.showScrollbars;

    const expandButtonVisibility = this._remotePlayerConfig?.expandButtonVisibility;

    // Prev/next buttons on the expanded playlist are always shown in landscape;
    // this toggle governs whether they also appear in portrait, where
    // the playlist would otherwise be swipe-only. Off by default; merchants opt
    // in per appearance.
    const showVerticalNavigationButtonsAttr = this.getAttribute('show-vertical-navigation-buttons');
    const showVerticalNavigationButtons = showVerticalNavigationButtonsAttr !== null
      ? showVerticalNavigationButtonsAttr === 'true'
      : (this._remotePlayerConfig?.showVerticalNavigationButtons ?? false);

    const showRowNavigationButtonsAttr = this.getAttribute('show-row-navigation-buttons');
    const showRowNavigationButtons = showRowNavigationButtonsAttr !== null
      ? showRowNavigationButtonsAttr === 'true'
      : (this._remotePlayerConfig?.showRowNavigationButtons ?? false);

    const remotePlayerSettingsOverrides = {
      ...(productCardMode !== undefined && { productCardMode }),
      ...(title !== undefined && { title }),
      ...(overlayTextWrap !== undefined && { overlayTextWrap }),
      ...(showScrollbars !== undefined && { showScrollbars }),
      ...(expandButtonVisibility !== undefined && { expandButtonVisibility }),
    };
    const remotePlayerSettingsOverridesString = getSettingsString(remotePlayerSettingsOverrides);

    const zoomOnHover = this.getAttribute('zoom-on-hover') !== null ?
      this.getAttribute('zoom-on-hover') === 'true' :
      (this._remotePlayerConfig?.zoomOnHover || false);

    // The playerJustify attribute is used to determine how the players should be justified in the container
    // Per https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
    const playerJustify = this.getAttribute('player-justify') || this._remotePlayerConfig?.playerJustify;
    const playerFit = this.getAttribute('player-fit') ||
      this._remotePlayerConfig?.playerFit ||
      BAM_PLAYER_FIT.SAME_HEIGHT;

    let mode = this.getAttribute('mode') || this._remotePlayerConfig?.mode || 'row';
    if (playerFit === BAM_PLAYER_FIT.FILL_PARENT && (softLimit === 1 || limit === 1)) {
      // The BAM_PLAYER_FIT.FILL_PARENT mode is only supported for single players
      // If the limit is set to 1, we should switch to single mode
      // If the softLimit is set to 1, we should switch to single mode
      // The mode 'single' is never exposed to the user, it's just an internal mode
      mode = 'single';
    }
    const isFab = mode === 'fab';

    const showPosterWhenPaused = this.getAttribute('show-poster-when-paused') !== null ?
      this.getAttribute('show-poster-when-paused') === 'true' :
      (this._remotePlayerConfig?.showPosterWhenPaused || false);

    const startMutedAttrValue = this.getAttribute('start-muted');
    const startMutedConfigValue = this._remotePlayerConfig?.startMuted;
    // bamAutoplayVideo opens the extended player without any prior user gesture, so force
    // muted playback to avoid the browser blocking autoplay with sound.
    const startMuted = bamAutoplayVideoId ? true :
      [true, false].map(String).includes(startMutedAttrValue) ?
        startMutedAttrValue === 'true' :
        (typeof startMutedConfigValue === 'boolean' && this._remotePlayerConfig?.startMuted) || false;

    const startFocusedAttrValue = this.getAttribute('start-focused');
    const startFocused = bamAutoplayVideoId ? true :
      [true, false].map(String).includes(startFocusedAttrValue) ?
        startFocusedAttrValue === 'true' :
        (this._remotePlayerConfig?.startFocused) || false;

    const fullscreen = this.getAttribute('fullscreen') === 'true';

    const closedCaptions = this.getAttribute('closed-captions')
        || this._remotePlayerConfig?.closedCaptions
        || CLOSED_CAPTIONS_MODE.OFF;

    // Soft preference for which subtitle track to default to when CC is "on".
    // Explicit locale pins stay strict (no track means no captions).
    const closedCaptionsLocaleHint = closedCaptions === CLOSED_CAPTIONS_MODE.ORIGINAL
      ? matchCcLocale(getLocale(), CC_LOCALES)
      : null;

    return {
      videosDataSource,
      channelId,
      eventIds,
      mode,
      // bamAutoplayVideo forces carousel so the extended player opens regardless of the
      // merchant's configured focus-mode (which can be playpause/none and never expand).
      focusMode: bamAutoplayVideoId
        ? FOCUS_MODE.CAROUSEL
        : (this.getAttribute('focus-mode') || this._remotePlayerConfig?.focusMode || FOCUS_MODE.CAROUSEL),
      autoplay: this.getAttribute('autoplay') || this._remotePlayerConfig?.autoplay || 'hover',
      videoIds,
      orgId,
      query: this.getAttribute('query') || 'auto',
      containerId: this.getRelevantContainerId(autoIndexId),
      // The player settings are a string of key-value pairs separated by semicolons
      // We have to combine the settings from the remote player config and the attributes
      // The attributes should take precedence (thus they are last in the string array)
      playerSettings: [
        remotePlayerSettingsOverridesString,
        this.getAttribute('player-settings')
      ].filter(Boolean).join(';'),
      zoomOnHover: isFab ? false : zoomOnHover,
      // A click on the collapsed playlist opens the first video from its first
      // frame (true | false). Read on click, so it needs no observing.
      expandFromStart: this.getAttribute('expand-from-start') === 'true',
      limit,
      softLimit,
      playerFit: isFab ? 'exact-size' : playerFit,
      landscapeInPortrait: this.getAttribute('landscape-in-portrait') ||
        this._remotePlayerConfig?.landscapeInPortrait ||
        SCALE_MODE.FIT,
      playerJustify: isFab ? 'left' : playerJustify,
      showPosterWhenPaused,
      startMuted,
      startFocused,
      cookieDomain: this.getAttribute('cookie-domain') ||
        this._remotePlayerConfig?.cookieDomain,
      cookieTTLDays: this.getAttribute('cookie-ttl-days') ||
        this._remotePlayerConfig?.cookieTTLDays,
      productAction: this.getAttribute('product-action')
        || this._remotePlayerConfig?.productAction
        || PRODUCT_ACTION_TYPE.EXPAND_PRODUCT,
      fullscreen,
      closedCaptions,
      closedCaptionsLocaleHint,
      startVideoId: bamAutoplayVideoId || this.getAttribute('start-video-id') || null,
      navigationMode: this.getAttribute('navigation-mode')
        || this._remotePlayerConfig?.navigationMode
        || NAVIGATION_MODE.NONE,
      showScrollbars,
      showVerticalNavigationButtons,
      showRowNavigationButtons,
    };
  }

  get autoIndexId() {
    const { orgId, _videosDataSource: videosDataSource } = this;
    // In the case that there are multiple bam-bundle elements on the page, we need to
    // somehow identify them, and chances are that the developer implementing the component
    // gave the elements an id which we can use to identify a specific component so we can distribute
    // and render the expected videos in the component.
    const nodeListArray = [
      ...Array.prototype.slice.call(document.getElementsByTagName('bam-bundle')), // legacy, to be removed
      ...Array.prototype.slice.call(document.getElementsByTagName('bam-playlist'))
    ].filter(component => {
      if (component === this) return true;

      // Scoping the incremented ids per video data source type (ex. vod or shows)
      if (component._videosDataSource !== videosDataSource) return false;

      // For vod its futher scoped per org id
      if (videosDataSource === 'vod' && component.orgId !== orgId) return false;

      return true;
    });
    const domNodeIndex = nodeListArray.indexOf(this);
    return domNodeIndex;
  }

  _updateStylesheet(playerRef) {
    if (!this._styleSheet) return;
    this.__updateStylesheetPlayerRef = playerRef;
    if (this.__updateStylesheetAF) return;
    this.__updateStylesheetAF = true;
    window.requestAnimationFrame(() => {
      this.__updateStylesheetAF = null;
      this.__updateStylesheet();
      this._updateRowNavigationState();
    });
  }

  __updateStylesheet() {
    const {
      zoomOnHover,
      softLimit,
      showScrollbars,
      showVerticalNavigationButtons,
      showRowNavigationButtons,
    } = this._attr;

    const {
      thumbnailSize = '68px',
      cornerRadius = '0px',
      playerWidth,
      playerHeight,
      playerPlaceholderColor = '#EEE',
      playerOverlayFontFamily,
      playerOverlayFontWeight = '700',
      playerOverlayTitleFontSize,
      playerOverlayTitleLineHeight,
      playerProductCardPadding,
      playerProductCardBackgroundColor,
      playerProductCardTitleWrap,
      playlistGap = '25px',
      playlistPadding = 0,
      playlistCarouselCornerRadius = '0px',
      playlistBackgroundColor = '#000',
    } = this._remotePlayerConfig || {};

    const defaultPlayerWidth = playerWidth || '250px';
    const defaultPlayerHeight = playerHeight || '444px';

    let playerFitStyles = '';

    switch (this._attr.playerFit) {
      case BAM_PLAYER_FIT.FILL_PARENT:
        playerFitStyles = `
          .wrapper:not(.is-expanded) bam-player {
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
          }
        `;
        break;
      case BAM_PLAYER_FIT.EXACT_SIZE:
        playerFitStyles = `
          .wrapper:not(.is-expanded) bam-player {
            width: var(--bam-player-width, ${defaultPlayerWidth});
            max-width: var(--bam-player-width, ${defaultPlayerWidth});
            height: var(--bam-player-height, ${defaultPlayerHeight});
            max-height: var(--bam-player-height, ${defaultPlayerHeight});
          }
        `;
        break;
      case BAM_PLAYER_FIT.SAME_WIDTH:
        playerFitStyles = `
          .wrapper:not(.is-expanded) bam-player {
            width: var(--bam-player-width, ${defaultPlayerWidth});
            max-width: var(--bam-player-width, ${defaultPlayerWidth});
          }
        `;
        break;
      case BAM_PLAYER_FIT.SAME_HEIGHT:
      default:
        playerFitStyles = `
          .wrapper:not(.is-expanded) bam-player {
            height: var(--bam-player-height, ${defaultPlayerHeight});
            max-height: var(--bam-player-height, ${defaultPlayerHeight});
          }
        `;
        break;
    }

    const scrollbarsStyle = showScrollbars ? `
      .player-container, .player-wrapper {
        scrollbar-width: var(--bam-playlist-scrollbar-width-mode, thin);
        scrollbar-color:
          var(--bam-playlist-scrollbar-thumb-color, rgba(0, 0, 0, 0.4))
          var(--bam-playlist-scrollbar-track-color, transparent);
        -ms-overflow-style: auto;
      }
      .player-container::-webkit-scrollbar, .player-wrapper::-webkit-scrollbar {
        display: block;
        width: var(--bam-playlist-scrollbar-thickness, 8px);
        height: var(--bam-playlist-scrollbar-thickness, 8px);
      }
      .player-container::-webkit-scrollbar-track, .player-wrapper::-webkit-scrollbar-track {
        background: var(--bam-playlist-scrollbar-track-color, transparent);
        border-radius: var(--bam-playlist-scrollbar-radius, 4px);
      }
      .player-container::-webkit-scrollbar-thumb, .player-wrapper::-webkit-scrollbar-thumb {
        background: var(--bam-playlist-scrollbar-thumb-color, rgba(0, 0, 0, 0.4));
        border-radius: var(--bam-playlist-scrollbar-radius, 4px);
      }
      .player-container::-webkit-scrollbar-thumb:hover, .player-wrapper::-webkit-scrollbar-thumb:hover {
        background: var(--bam-playlist-scrollbar-thumb-hover-color, rgba(0, 0, 0, 0.7));
      }
    ` : `
      .player-container::-webkit-scrollbar, .player-wrapper::-webkit-scrollbar {
        display: none;
      }

      .player-container, .player-wrapper {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
    `;

    // In portrait the playlist scrolls vertically, so the prev/next buttons sit
    // stacked at the center-right with chevrons rotated to point up/down. Center
    // alignment keeps them clear of the in-player control column (top) and the
    // product card (bottom) at every viewport. Landscape is unaffected — it shows
    // the buttons unconditionally.
    //
    // The buttons are always rendered and keyboard-focusable in portrait so
    // screen-reader and keyboard users can always page between videos. The
    // appearance toggle only controls whether they are visible at rest: with it
    // off they are hidden with opacity (never display:none, which would drop them
    // from the tab order and make them unfocusable) and revealed as a pair when
    // either receives keyboard focus (:has(:focus-visible) on the wrapper).
    //
    // The right inset tracks the video's edge, which differs by platform: on
    // touch the expanded player is edge-to-edge (full-width), while on desktop
    // it is a centered card capped at 90vw — so we inset past that 5vw letterbox
    // to keep the buttons on the video rather than out in the backdrop.
    const navInsetRight = this._isDesktop ? 'calc(5vw + 16px)' : '16px';
    const navHiddenUntilFocus = showVerticalNavigationButtons ? '' : `
          opacity: 0;
          pointer-events: none;`;
    const portraitNavigationStyle = `
      @media (orientation: portrait) {
        .wrapper.is-expanded .prev-button, .wrapper.is-expanded .next-button {
          display: flex;
          right: ${navInsetRight};
          ${navHiddenUntilFocus}
        }

        .wrapper.is-expanded:has(.prev-button:focus-visible, .next-button:focus-visible) .prev-button,
        .wrapper.is-expanded:has(.prev-button:focus-visible, .next-button:focus-visible) .next-button {
          opacity: 1;
          pointer-events: auto;
        }

        .wrapper.is-expanded:not(:has(.player-container > .player-wrapper:nth-child(2))) .prev-button,
        .wrapper.is-expanded:not(:has(.player-container > .player-wrapper:nth-child(2))) .next-button {
          display: none;
        }

        .wrapper.is-expanded .prev-button {
          top: 50%;
          transform: translateY(calc(-50% - 26px));
        }

        .wrapper.is-expanded .next-button {
          top: 50%;
          transform: translateY(calc(-50% + 26px));
        }

        .wrapper.is-expanded .prev-button svg, .wrapper.is-expanded .next-button svg {
          transform: rotate(90deg);
        }
      }
    `;

    const rowNavigationHiddenUntilFocus = showRowNavigationButtons ? '' : `
          opacity: 0;
          pointer-events: none;`;
    const rowNavigationDisabledOpacity = showRowNavigationButtons ? `
      .wrapper.row-has-overflow:not(.is-expanded):not(.is-expanded-single) button[aria-disabled="true"] {
        opacity: 0.5;
      }` : `
      .wrapper.row-has-overflow:not(.is-expanded):not(.is-expanded-single):has(
        .prev-button:focus-visible,
        .next-button:focus-visible
      ) button[aria-disabled="true"] {
        opacity: 0.5;
      }`;
    const rowNavigationStyle = `
      .wrapper:not(.is-expanded):not(.is-expanded-single) {
        position: relative;
      }

      .wrapper:not(.is-expanded):not(.is-expanded-single) .prev-button,
      .wrapper:not(.is-expanded):not(.is-expanded-single) .next-button {
        top: 50%;
        transform: translateY(-50%);
        ${rowNavigationHiddenUntilFocus}
      }

      .wrapper.row-has-overflow:not(.is-expanded):not(.is-expanded-single) .prev-button,
      .wrapper.row-has-overflow:not(.is-expanded):not(.is-expanded-single) .next-button {
        display: flex;
      }

      .wrapper:not(.is-expanded):not(.is-expanded-single):has(
        .prev-button:focus-visible,
        .next-button:focus-visible
      ) .prev-button,
      .wrapper:not(.is-expanded):not(.is-expanded-single):has(
        .prev-button:focus-visible,
        .next-button:focus-visible
      ) .next-button {
        opacity: 1;
        pointer-events: auto;
      }

      .wrapper.row-has-overflow:not(.is-expanded):not(.is-expanded-single) button[aria-disabled="true"] {
        cursor: default;
      }

      ${rowNavigationDisabledOpacity}

      .wrapper:not(.is-expanded):not(.is-expanded-single) .prev-button {
        left: var(--row-navigation-left, 10px);
      }

      .wrapper:not(.is-expanded):not(.is-expanded-single) .next-button {
        right: var(--row-navigation-right, 10px);
      }

    `;

    let modeStyles = '';
    switch (this._attr.mode) {
      case 'single':
        const hostStyles = this._attr.playerFit === BAM_PLAYER_FIT.FILL_PARENT ? `
          :host,
          .wrapper:not(.is-expanded),
          .wrapper:not(.is-expanded) .player-container,
          .wrapper:not(.is-expanded) .player-container .player-wrapper {
            width: 100%;
            height: 100%;
          }
          ` : `
          :host {
            width: fit-content;
          }`;

        modeStyles = `
          ${hostStyles}
          ${scrollbarsStyle}
          ${playerFitStyles}
          .wrapper:not(.is-expanded) .player-container {
            display: flex;
          }
          .wrapper:not(.is-expanded) .player-container .player-wrapper:not(:first-child) {
            display: none;
          }
        `;
        break;
      case 'fab':
        modeStyles = `
          :host {
            width: var(--bam-player-width, ${defaultPlayerWidth});
            height: var(--bam-player-height, ${defaultPlayerHeight});
            overflow: hidden;
            display: flex;
            flex-direction: row;
            justify-content: center;
            position: fixed;
            bottom: 10px;
            right: 10px;
            z-index: 2147483647;
          }
          ${scrollbarsStyle}
          .wrapper:not(.is-expanded) .player-container {
            overflow: hidden;
            gap: 0;
          }
          .wrapper.is-expanded .player-container {
            display: flex;
            flex-wrap: nowrap;
          }
          ${playerFitStyles}
        `;
        break;
      case 'grid':
        modeStyles = `
          ${scrollbarsStyle}
          .player-container {
            flex-wrap: wrap;
          }
          .wrapper.is-expanded .player-container {
            display: flex;
            flex-wrap: nowrap;
          }
          ${playerFitStyles}
        `;
        break;
      case 'hidden':
        modeStyles = `
          .wrapper {
            display: none;
          }
        `;
        break;
      case 'row':
      default:
        modeStyles = `
          :host {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: center;
          }
          ${scrollbarsStyle}
          ${playerFitStyles}
          .wrapper:not(.is-expanded):not(.is-expanded-single) .player-container {
            overflow-x: scroll;
          }
          ${rowNavigationStyle}
        `;
        break;
    }

    const shouldZoomOnHover = !this._isExpanded && zoomOnHover;

    const productCardBackgroundColorStyle = playerProductCardBackgroundColor
      ? `--internal-product-card-background-color: ${playerProductCardBackgroundColor};`
      : '';
    const productCardTitleWrapStyle = playerProductCardTitleWrap
      ? `
        --internal-product-card-title-display: -webkit-box;
        --internal-product-card-title-white-space: normal;
        --internal-product-card-title-line-clamp: 2;
      `
      : '';

    // Card padding is a free-text field; a bare number (e.g. "0") is not a valid
    // CSS length, which breaks the product-info edge-inset math. Give it a unit.
    const normalizedProductCardPadding = /^-?\d*\.?\d+$/.test(String(playerProductCardPadding).trim())
      ? `${playerProductCardPadding}px`
      : playerProductCardPadding;

    let autoDetectedFontFamily;
    const h1FontFamily = getPageStyleOfTagsWithName('h1', 'fontFamily');
    if (h1FontFamily) {
      autoDetectedFontFamily = h1FontFamily;
    }

    this._styleSheet.textContent = `
      :host {
        display: block;
        --remote-config-thumbnail-size: ${thumbnailSize ?? '68px'};
        --effective-thumbnail-size: var(--bam-player-thumbnail-size, var(--remote-config-thumbnail-size, 68px));
        ${autoDetectedFontFamily ? `--bam-player-auto-detected-overlay-font-family: ${autoDetectedFontFamily}` : ''};
        --internal-font-family: var(--bam-player-overlay-font-family, ${playerOverlayFontFamily ?? `var(--bam-player-auto-detected-overlay-font-family, sans-serif)`});
        --internal-corner-radius: ${cornerRadius};
        --internal-overlay-title-font-size: ${playerOverlayTitleFontSize};
        --internal-overlay-title-line-height: ${playerOverlayTitleLineHeight};
        ${playerWidth ? `--internal-width: ${playerWidth};` : ''}
        ${playerHeight ? `--internal-height: ${playerHeight};` : ''}
        --internal-overlay-font-weight: ${playerOverlayFontWeight};
        --internal-placeholder-color: ${playerPlaceholderColor};
        --scroll-svg-color: var(--bam-bundle-scroll-svg-color, #000);
        ${playerProductCardPadding ? `--internal-product-card-padding: ${normalizedProductCardPadding};` : ''}
        ${productCardBackgroundColorStyle}
        ${productCardTitleWrapStyle}
        --internal-background-color: var(--bam-playlist-background-color, ${playlistBackgroundColor});
      }
      .wrapper {
        width: 100%;
      }
      .player-container {
        display: flex;
        position: relative;
        padding: 0;
        margin: 0;
        list-style: none;
      }
      .player-wrapper {
        display: inline-block;
      }
      .close-button, .fab-preview-close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        padding: 10px;
        z-index: 10000001;
        cursor: pointer;
        display: none;
        color: white;
        text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
        font-size: 32px;
        opacity: 0.9;
        user-select: none;
        background: none;
        border: none;
        font-family: 'Courier New', Courier, monospace;
      }
      .close-button {
        right: 16px;
        padding-left: 14px;
        padding-right: 14px;
      }
      .fab-preview-close-button {
        top: var(--bam-playlist-fab-close-button-offset, 10px);
        right: var(--bam-playlist-fab-close-button-offset, 10px);
        padding: var(--bam-playlist-fab-close-button-padding, 10px);
        font-size: var(--bam-playlist-fab-close-button-size, 32px);
      }
      .load-more-button {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px;
        position: relative;
        overflow: hidden;
        overscroll-behavior: none;
        font-weight: var(--bam-player-overlay-font-weight, ${playerOverlayFontWeight});
        border-radius: var(--bam-player-corner-radius, ${cornerRadius});
        font-family: var(--internal-font-family), Helvetica, Arial, Verdana, Tahoma, sans-serif;
        cursor: pointer;
        color: black;
        flex-shrink: 0;
        user-select: none;
      }
      .close-button:hover,
      .fab-preview-close-button:hover,
      .load-more-button:hover {
        opacity: 0.8;
      }
      .wrapper:not(.is-expanded) .player-container {
        gap: var(--bam-playlist-gap, var(--bam-bundle-gap, ${playlistGap}));
        padding: var(--bam-playlist-padding, ${playlistPadding});
        ${ this._attr.playerJustify ? `justify-content: ${this._attr.playerJustify};` : '' }
      }
      .wrapper:not(.is-expanded) bam-player {
        cursor: pointer;
        z-index: 1;
      }
      ${!shouldZoomOnHover ? '' : `
        .wrapper:not(.is-expanded) .player-container {
          padding: 50px 0;
          padding-left: calc(0.03 * var(--bam-player-width, ${defaultPlayerWidth}));
          padding-left: calc(round(up, 0.03 * var(--bam-player-width, ${defaultPlayerWidth}), 1px));
          padding-right: calc(0.03 * var(--bam-player-width, ${defaultPlayerWidth}));
          padding-right: calc(round(up, 0.03 * var(--bam-player-width, ${defaultPlayerWidth}), 1px));
        }
        .wrapper:not(.is-expanded) .player-wrapper {
          transition: transform 300ms ease;
        }
        .wrapper:not(.is-expanded) .player-wrapper:hover {
          z-index: 10;
          transform: scale(1.06);
        }
        .wrapper:not(.is-expanded) .player-wrapper:hover bam-player {
          transition: box-shadow 300ms ease-in;
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
        }
      `}
      .wrapper.is-expanded {
        z-index: 10000000;
        inset: 0px;
        position: fixed;
        height: 100%;
        display: block;
      }
      .wrapper.is-expanded .player-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-content: center;
        align-items: flex-start;
        height: 100%;
        order: var(--order);

        min-height: 80vh;
        scroll-snap-align: center;
        scroll-snap-stop: always;
      }
      ${!softLimit && softLimit < Infinity ? '' : `
        .wrapper:not(.is-expanded) .player-wrapper:nth-child(n+${softLimit + 1}) {
          display: none;
        }
      `}
      .wrapper.is-expanded .dimmer {
        position: absolute;
        inset: 0px;
        background-color: var(--internal-background-color, #000);
        z-index: -1;
      }
      .wrapper.is-expanded .player-container {
        white-space: nowrap;
        position: absolute;
        height: 100%;
        column-gap: 25px;

        box-sizing: border-box;
        max-height: 100vh;
        padding-top: 50vh;
        padding-bottom: 50vh;
        overflow: auto;
        scroll-snap-type: both mandatory;
        overscroll-behavior: contain;
      }
      .wrapper.is-expanded .close-button {
        display: block;
      }
      .wrapper.is-expanded.hide-close-button .close-button {
        display: none;
      }
      .wrapper:not(.is-expanded):not(.is-expanded-single) .fab-preview-close-button {
        display: block;
      }
      .wrapper.is-expanded .player-wrapper:not(.focused) {
        opacity: 0.2;
        transition: opacity 0.5s;
      }
      .wrapper.is-expanded .player-wrapper:not(.focused) bam-player {
        cursor: pointer;
      }
      /* touch devices will not have hover */
      ${!this._isDesktop ? `
        @media (orientation: portrait) {
          .wrapper.is-expanded {
            justify-content: flex-start;
          }
          .wrapper.is-expanded .player-container {
            width: 100%;
            flex-direction: column;
            height: auto;
            height: calc(var(--vh, 1vh) * 100);

            min-height: 100vh;
            min-height: calc(var(--vh, 1vh) * 100);

            padding-top: unset;
            padding-bottom: unset;
          }
          .wrapper.is-expanded .player-wrapper {
            width: 100%;
            inset: 0px;
            margin: 0;
            height: 100vh; /* Fallback for browsers that do not support custom css properties */
            height: calc(var(--vh, 1vh) * 100);

            min-height: 100vh;
            min-height: calc(var(--vh, 1vh) * 100);
          }
          .wrapper.is-expanded bam-player {
            border-radius: 0;
            inset: 0px;
            width: 100%;
            height: 100%;
            max-width: none;
            --bam-player-placeholder-color: #000;
          }
        }` : `
        /* non-touch devices will have hover */
        @media (orientation: portrait) {
          .wrapper.is-expanded .player-container {
            height: auto;
            width: 100%;
            flex-direction: column;
            row-gap: 25px;
          }
          .wrapper.is-expanded .player-wrapper {
            height: 80vh;
            align-items: center;
          }
          .wrapper.is-expanded bam-player {
            max-height: 80vh;
            max-width: 90vw;
            height: 100%;
            width: auto;
            border-radius: var(--bam-playlist-carousel-corner-radius, var(--bam-bundle-carousel-corner-radius, ${playlistCarouselCornerRadius}));
            --bam-player-placeholder-color: transparent;
          }
        }
      `}
      @media (orientation: landscape) {
        .player-container {
          flex-direction: row;
        }

        .player-container:dir(rtl)  {
          flex-direction: row-reverse;
        }

        .wrapper.safari-rtl-fix {
          direction: ltr;
        }
        .wrapper.safari-rtl-fix .player-container {
          direction: ltr;
          flex-direction: row;
        }

        .wrapper.is-expanded .player-container {
          max-width: 100vw;

          padding-left: 50vw;
          padding-right: 50vw;

          overflow-y: hidden;
        }

        .wrapper.is-expanded bam-player {
          max-height: 80vh;
          max-width: 80vw;
          height: 100%;
          width: auto;

          /*
            If a player is slimmer than this and expected to present interactive UI the player is expected
            to be presented in an accessibility type of way, and the player will apply a overflow scroll type
            which lets the user scroll around in the player to see all elements, which is not what we want when
            presented in a carousel type of way.
          */
          min-width: 320px;
          border-radius: var(--bam-playlist-carousel-corner-radius, var(--bam-bundle-carousel-corner-radius, ${playlistCarouselCornerRadius}));

          --bam-player-placeholder-color: transparent;
        }
        .wrapper.is-expanded .load-more-button {
          color: white;
        }
      }
      ${modeStyles}

      .wrapper .next-button, .wrapper .prev-button {
        display: none;
        justify-content: center;
        align-items: center;
        position: absolute;
        padding: 10px;
        background-color: rgba(255, 255, 255, 0.5);
        border: none;
        color: white;
        cursor: pointer;
        z-index: 10;
        border-radius: 100%;
        user-select: none;
      }

      .wrapper .next-button svg, .wrapper .prev-button svg {
        width: 20px;
        height: 20px;
        color: var(--scroll-svg-color);
      }

      .wrapper .next-button:hover, .wrapper .prev-button:hover {
        background-color: rgba(255, 255, 255, 0.8);
      }

      @media (orientation: landscape) {
        .wrapper.is-expanded .next-button, .wrapper.is-expanded .prev-button {
          display: flex;
          top: 50%;
          transform: translateY(-50%);
        }

        .wrapper.is-expanded:not(:has(.player-container > .player-wrapper:nth-child(2))) .prev-button,
        .wrapper.is-expanded:not(:has(.player-container > .player-wrapper:nth-child(2))) .next-button {
          display: none;
        }

        .wrapper.is-expanded .next-button {
          right: 10px;
        }

        .wrapper.is-expanded:dir(rtl) .next-button {
          right: 10px;
          left: unset;
          transform: translateY(-50%);
        }

        .wrapper.is-expanded .prev-button {
          left: 10px;
        }

        .wrapper.is-expanded:dir(rtl) .prev-button {
          left: 10px;
          right: unset;
          transform: translateY(-50%);
       }

        .wrapper.is-expanded .load-more-button {
          display: none;
        }
      }
      ${portraitNavigationStyle}
    `;


    // If playerMode is 'single' we need to make sure we handle the z space correctly for the players
    // to make sure the relevant player is on top when expanded, and the other ones are places underneath
    if (this._attr.focusMode === FOCUS_MODE.SINGLE && this._players) {
      for (const player of this._players) {
        if (player === this.__updateStylesheetPlayerRef) {
          player.style.zIndex = 1;
        } else {
          player.style.zIndex = 0;
        }
      }
    }
  }

  _bumpVhCSSVariable() {
    const vh = window.innerHeight * 0.01;
    this._wrapper.style.setProperty('--vh', `${vh}px`);
  }

  _maybeOverrideJustifyCenter() {
    // player-justify=center only works nicely if the players fit inside the container without scrolling.
    // If not, it's prettier to align the players to the start of the container.
    if (!this._playerContainer || this._attr.playerJustify !== 'center') return;
    if (this._playerContainer.scrollWidth > this._playerContainer.clientWidth) {
      this._playerContainer.style.justifyContent = 'start';
    } else {
      this._playerContainer.style.justifyContent = '';
    }
  }

  _updateRowNavigationState() {
    if (!this._wrapper || !this._playerContainer) return;

    const isRowPreview = this._attr.mode === 'row' && !this._isExpanded;
    const prevButton = this._prevButton;
    const nextButton = this._nextButton;
    if (!prevButton || !nextButton) return;

    if (!isRowPreview) {
      this._wrapper.classList.remove('row-has-overflow');
      this._wrapper.style.removeProperty('--row-navigation-left');
      this._wrapper.style.removeProperty('--row-navigation-right');
      prevButton.ariaDisabled = 'false';
      nextButton.ariaDisabled = 'false';
      return;
    }

    const players = this._players || [];
    const visiblePlayers = players.slice(0, this._attr.softLimit || players.length);
    const edgePlayers = [visiblePlayers[0], visiblePlayers.at(-1)]
      .filter((player, index, values) => player && values.indexOf(player) === index);
    const playerBounds = edgePlayers.map((player) => player.parentElement?.getBoundingClientRect());
    const containerBounds = this._playerContainer.getBoundingClientRect();
    const navigationInsets = getRowNavigationInsets({
      containerBounds,
      viewportWidth: document.documentElement.clientWidth,
    });
    const { canScrollLeft, canScrollRight } = getRowNavigationState({
      containerBounds,
      playerBounds,
    });
    this._wrapper.classList.toggle('row-has-overflow', canScrollLeft || canScrollRight);
    prevButton.ariaDisabled = String(!canScrollLeft);
    nextButton.ariaDisabled = String(!canScrollRight);
    this._wrapper.style.setProperty('--row-navigation-left', `${navigationInsets.left}px`);
    this._wrapper.style.setProperty('--row-navigation-right', `${navigationInsets.right}px`);
  }

  _applyPlayerSettingsUpdate() {
    const { playerSettings: playerSettingsAttributeValue } = this._attr;
    if (!playerSettingsAttributeValue) return;
    if (!this._players?.length) return;

    this._players.forEach((player) => {
      player.setAttribute('settings', playerSettingsAttributeValue);
    });
  }

  _updatePlayersSettingsFromAttributes() {
    const { playerSettings: playerSettingsAttributeValue } = this._attr;
    if (!playerSettingsAttributeValue) return;

    this._playerSettings = { ...this._playerSettings, ...parsePlayerSettings(playerSettingsAttributeValue)};
  };

  _isVisibleOnPage() {
    if ('checkVisibility' in this) {
      if (!this.checkVisibility()) {
        // The element is not visible even though it may be in the viewport
        return false;
      }
    }
    const { top, bottom } = this.getBoundingClientRect();
    return top < window.innerHeight && bottom > 0;
  }

  async _setup() {
    this._emit('setup');

    this._expanded = false;

    // eslint-disable-next-line no-multi-assign
    const setupId = this._setupIdIndex = (this._setupIdIndex || 0) + 1;
    this._lastSetupId = setupId;

    // If below the fold, defer loading the player until the browser is idle or
    // the DOM is ready (depending on the browser capabilities)
    if (!this._didDefer && !this._isVisibleOnPage()) {
      this._didDefer = true;
      if ('requestIdleCallback' in window) {
        // Defer loading the player until the browser is idle
        window.requestIdleCallback(() => {
          this._setup();
        }, {
          timeout: 5000, // Don't wait more than 5 seconds
        });
        return;
      } else if (document.readyState === 'loading') {
        // Defer until the DOM is ready
        document.addEventListener('DOMContentLoaded', () => this._setup());
        return;
      }
    }

    const initialAttr = this._attr;
    if (debugMode) {
      emitDebugMessage(this._debugId, 'bam-playlist:setup', { setupId, attributes: initialAttr });
    }

    let playlist = [];
    let playlistMetadata = [];
    try {
      const {
        playlist: actualPlaylist,
        playlistMetadata: actualPlaylistMetadata,
        playerConfig: backendPlayerConfig,
        distributionPageId,
        distributionContainerId,
        pageSize,
        total,
        page,
        playlistId,
        appearanceGroupId,
        appearanceId,
        placementId,
      } = await loadPlaylist({
        ...initialAttr,
        presentationContext: this._presentationalContext,
        appearanceGroupOverride: this._externallySetAppearanceConfig,
      });

      const actualPlayerConfig = {
        ...backendPlayerConfig,
        ...(this._externallySetPlayerConfig || {}),
      };

      if (debugMode) {
        emitDebugMessage(this._debugId, 'bam-playlist:loaded', {
          setupId,
          playlist: actualPlaylist,
          playlistMetadata: actualPlaylistMetadata,
          playerConfig: actualPlayerConfig,
          distributionPageId,
          distributionContainerId,
          pageSize,
          total,
          page,
          playlistId,
          appearanceGroupId,
          appearanceId,
          placementId,
        });
      }

      if (actualPlayerConfig.mode === 'story') {
        actualPlayerConfig.mode = 'row';
      }

      playlist = actualPlaylist;
      playlistMetadata = actualPlaylistMetadata;
      // themeId is also surfaced separately as _themeId (forwarded to each
      // bam-player via the theme-id attribute), but keep it on _remotePlayerConfig
      // too — normalized so "no theme" is '' — so the applyPlayerConfig diff has a
      // complete baseline and an unchanged theme doesn't read as changed.
      this._remotePlayerConfig = { ...actualPlayerConfig, themeId: actualPlayerConfig.themeId || '' };
      this._themeId = actualPlayerConfig.themeId || null;
      this._distributionPageId = distributionPageId;
      this._distributionContainerId = distributionContainerId;
      this._pageSize = pageSize;
      this._total = total;
      this._page = page;
      this._hasNextPage = total > page * pageSize;
      this._playlistId = playlistId;
      this._appearanceGroupId = appearanceGroupId;
      this._appearanceId = appearanceId;
      this._placementId = placementId;
    } catch (err) {
      this._printError('Failed to load playlist', {
        err,
      });
    }
    if (this._lastSetupId !== setupId) {
      // Another setup has been started since this one, so let's return and allow the newer setup to take over.
      this._printInfo('Another setup has been triggered, bailing', { setupId, lastSetupId: this._lastSetupId });
      return;
    }

    // If ?bamAutoplayVideo targets a video the loaded playlist doesn't contain, drop the
    // override so we render the merchant's playlist normally instead of replacing it with
    // a single video that fails to load (which would tear down the whole component).
    if (this._bamAutoplayVideoId && !playlist.includes(this._bamAutoplayVideoId)) {
      this._bamAutoplayVideoId = null;
    }

    if (this._attr.mode === 'fab') {
      // Detect if we have any 'real' type FAB components on the site
      const floatingWidgets = document.querySelectorAll('[data-bambuser-liveshopping-floating-id]');
      if (floatingWidgets?.length && Array.from(floatingWidgets).some((widget) => widget.style.display !== 'none')) {
        // We have 'real' FAB components on the site which is loaded and visible, so we do not want to setup this one
        this._printInfo('There are already a FAB components on the site, skipping setup');
        return;
      } else if (window.__bSetupBamPlaylistFabResolver) {
        // We're already have a callback function set up, so we're waiting for the 'real' FAB component to be loaded
        this._printInfo('Waiting for the FAB component to be loaded, skipping setup');
        return;
      } else if (window.__bfwId && !window.__bSetupBamPlaylistFabResolver && !window.__bFabHasInitiallyLoaded) {
        // We found hints that the FAB widget embed code is being loaded, but 'real' FAB container have not yet to been setup properly
        // Let's set up a callback function which the 'real' FAB widget can call when it's received information about the display state of
        // the 'real' FAB widget so we can trigger the appropriate setup of the bam-playlist FAB component, if needed.

        await new Promise((resolve) => window.__bSetupBamPlaylistFabResolver = resolve);
      }

      // If the mode is 'fab', we need to make sure the component is not burried deep down in the DOM heirarchy
      // to avoid z-index issues with other components. If we keep it at the top level of the body, we'll easier
      // be able to control the z-index of the component compared to siblings elements.

      // Only allow one fab-mode bam-playlist on the page at any given time
      // If the fab component already is in the body, then there's no reason to move it
      const bodyBamPlaylists = Array.from(document.body.querySelectorAll(':scope > bam-playlist'));
      const bodyBamFabPlaylists = bodyBamPlaylists.filter((bp) => bp._attr?.mode === 'fab');
      const bodyContainsBamFabPlaylistSelf = bodyBamFabPlaylists.some((bp) => bp === this);
      if (bodyBamFabPlaylists.length === 0) {
        // We do not have a bam-playlist with the mode 'fab' in the DOM, so we add it.
        // We currently only allow one of these on the page at any given time.
        this._printInfo('Adding FAB component');
        document.body.appendChild(this);
      } else if (!bodyContainsBamFabPlaylistSelf) {
        // We already have a bam-playlist with the mode 'fab' in the DOM, so we lets leave this one.
        // REMARK: Alternative approach could be to re-configure this one to be presented in some other mode.
        this._printInfo('There already exists a FAB component, skipping setup');
        return;
      }
    }

    const {
      focusMode,
      autoplay,
      limit,
      softLimit,
      playerFit,
      mode,
      landscapeInPortrait,
      showPosterWhenPaused,
      startMuted,
      startFocused,
      cookieDomain,
      cookieTTLDays,
      productAction,
      closedCaptions,
      closedCaptionsLocaleHint,
      navigationMode,
    } = this._attr;

    if (![FOCUS_MODE.CAROUSEL, FOCUS_MODE.SINGLE, FOCUS_MODE.PLAYPAUSE, FOCUS_MODE.NONE].includes(focusMode)) {
      this._printError('Invalid "focusMode" attribute. Must be either "carousel", "single", "playpause" or "none".');
      return;
    }

    if (startFocused && ![FOCUS_MODE.CAROUSEL, FOCUS_MODE.SINGLE].includes(focusMode)) {
      this._printError('The "start-focused" attribute can only be used with focus mode "carousel" or "single".');
      return;
    }

    if (!['cascade', 'hover', 'none'].includes(autoplay)) {
      this._printError('Invalid "autoplay" attribute. Must be either "cascade" or "hover" or "none".');
      return;
    }

    if (!Object.values(BAM_PLAYER_FIT).includes(playerFit)) {
      this._printError(`Invalid "playerFit" attribute. Must be either ${Object.values(BAM_PLAYER_FIT).join(" or ")}.`);
      return;
    }

    if (playerFit === BAM_PLAYER_FIT.FILL_PARENT && !(limit === 1 || softLimit === 1 || mode === 'single')) {
      this._printError('The "fill-parent" player fit mode can only be used with a limit of 1 or soft limit of 1 or mode "single".');
      return;
    }

    if (limit === 0) {
      this._printError('The limit is set to 0, so no players will be rendered.');
      return;
    }

    if (productAction && !Object.values(PRODUCT_ACTION_TYPE).includes(productAction)) {
      this._printError(`Invalid "productAction" attribute. Must be one of ${Object.values(PRODUCT_ACTION_TYPE)}.`);
      return;
    }

    const isValidClosedCaptions = (value) =>
      Object.values(CLOSED_CAPTIONS_MODE).includes(value) ||
      CC_LOCALES.has(value);

    if (closedCaptions && !isValidClosedCaptions(closedCaptions)) {
      // Non critical error, so we log it but continue
      this._printError(
        `Invalid "closedCaptions" attribute. Must be one of ${Object.values(CLOSED_CAPTIONS_MODE)}`
        + ' or a valid language code (e.g. "en-US", "fr-FR").',
      );
    }

    // Make sure we start from a clean slate
    this.shadow.innerHTML = '';
    this._stopAutoplayCascade();

    if (!playlist.length) {
      this.dataset.status = 'empty';
      return;
    }

    this._updatePlayersSettingsFromAttributes();

    // Setup DOM elements
    const styleSheet = document.createElement('style');
    this._styleSheet = styleSheet;

    const wrapper = document.createElement('div');
    wrapper.classList.add('wrapper');
    this._wrapper = wrapper;

    // Safari 18 or have some compatibility issues with rtl mode,
    // And this breaks the layout rendering the bam-playlist unusable.
    // As as workaround we apply a fix which neutralizes the rtl mode.
    // TODO: Remove this when rtl mode is fully supported
    try {
      const ua = window?.navigator?.userAgent;
      if (!ua) return;
      const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Android/.test(ua);
      let applySafariRtlFix = false;
      if (isSafari) {
        const m = ua.match(/Version\/(\d+)\./);
        if (m) {
          const major = parseInt(m[1], 10);
          applySafariRtlFix = Number.isFinite(major) && major < 18;
        }
      }
      if (applySafariRtlFix) wrapper.classList.add('safari-rtl-fix');
    } catch (_) {
      this._printError('Failed to apply Safari RTL fix');
    }

    const dimmer = document.createElement('div');
    dimmer.classList.add('dimmer');

    const playerContainer = document.createElement('ul');
    playerContainer.classList.add('player-container');
    this._playerContainer = playerContainer;

    const closeButton = document.createElement('button');
    closeButton.classList.add('close-button');
    closeButton.setAttribute('data-default-focus','');

    const closeButtonTextContainer = document.createElement('span');
    closeButtonTextContainer.textContent = '✕';
    closeButtonTextContainer.setAttribute('aria-hidden', 'true');
    closeButton.appendChild(closeButtonTextContainer);

    closeButton.addEventListener('click', () => this._contract());

    // Add previous and next buttons
    // Default accessible names so the buttons always have a label; overwritten
    // by merchant customizations (prevAriaLabel/nextAriaLabel) in
    // _applyCustomizations once customizations-loaded fires.
    const prevButton = document.createElement('button');
    prevButton.innerHTML = ChevronLeft;
    prevButton.classList.add('prev-button');
    prevButton.ariaLabel = 'Previous';
    prevButton.addEventListener('click', this._onPrevious.bind(this));
    this._prevButton = prevButton;

    const nextButton = document.createElement('button');
    nextButton.innerHTML = ChevronRight;
    nextButton.classList.add('next-button');
    nextButton.ariaLabel = 'Next';
    nextButton.addEventListener('click', this._onNext.bind(this));
    this._nextButton = nextButton;


    this.shadow.appendChild(styleSheet);
    this.shadow.appendChild(wrapper);
    wrapper.appendChild(dimmer);
    wrapper.appendChild(closeButton);
    wrapper.appendChild(prevButton);
    wrapper.appendChild(nextButton);
    wrapper.appendChild(playerContainer);

    if (this._attr.mode === 'fab') {
      this._addFabPreviewCloseButton(wrapper);
    }
    if (this._videosDataSource === 'vod') {
      // If we have yet to reach the player limit, we can load more videos
      if (this._hasNextPage && playlist.length < Math.min(limit, softLimit)) {
        const loadMoreButton = document.createElement('div');
        loadMoreButton.classList.add('load-more-button');
        loadMoreButton.textContent = this._customizations?.generic?.loadMoreLabel ?? 'Load more';
        loadMoreButton.addEventListener('click', this._loadNextPaginatedVideos);
        const computedStyle = window.getComputedStyle(wrapper);
        loadMoreButton.style.order = computedStyle.direction === 'rtl'
          ? Number.MIN_SAFE_INTEGER
          : Number.MAX_SAFE_INTEGER;
        playerContainer.appendChild(loadMoreButton);
        this._loadMoreButton = loadMoreButton;
      }
    }

    // Respect the optional start-video-id attribute
    const startVideoId = this._attr.startVideoId;
    const startVideoIndex = startVideoId ? playlist.findIndex((videoId) => videoId === startVideoId) : 0;
    if (startVideoId && startVideoIndex === -1) {
      // The loaded playlist does not contain the video we want to start with.
      // It's more important to show the intended start video than to show the
      // rest of the playlist, so we override the playlist entirely in this case.
      playlist = [ startVideoId ];
    }
    if (startVideoIndex > 0) {
      // Rotate the playlist so the intended start video is first in line.
      // When operating in carousel mode, the order CSS property will be used
      // to visually position the players in the correct order.
      playlist = [
        ...playlist.slice(startVideoIndex),
        ...playlist.slice(0, startVideoIndex),
      ];
    }

    this._updateStylesheet();
    this._determineScrollDirection();

    this._playerContainer.addEventListener('scroll', throttle((e) => this._onScroll(e), 50), { passive: true });

    // Create a bam-player for each show in the playlist
    this._players = playlist.slice(0, limit).map((eventId, index) => this._createBamPlayer({
      eventId,
      focusMode,
      autoplay,
      isSingleVideo: playlist.length === 1,
      playerFit,
      mediaAssets: playlistMetadata?.find(videoMetadata => videoMetadata.id === eventId)?.mediaAssets,
      landscapeInPortrait,
      showPosterWhenPaused,
      startMuted,
      cookieDomain,
      cookieTTLDays,
      softLimit,
      productAction,
      closedCaptions,
      closedCaptionsLocaleHint,
      inPlaylistIndex: index,
      playlistLength: this._total ? this._total - 1 : 0,
      navigationMode,
    }));

    if (startVideoIndex > 0) {
      // Set the "order" CSS property on all players to match the original
      // order, meaning the players that were initially before the intended
      // start video (which is now at position 0) gets negative order values
      // matching their original position in the playlist.
      // Example:
      //   Original playlist:      [ A, B, C, D, E ]
      //   startVideoId = C
      //   Rotated playlist:      [ C, D, E, A, B ]
      //   Player orders should be: [ 0, 1, 2, -2, -1 ]
      this._players.forEach((player, index) => {
        const order = index <= this._players.length - startVideoIndex - 1
          ? index
          : index - this._players.length;
        player.parentElement.style.setProperty('--order', order);
      });
    }

    // Update the status attribute when the first player has loaded
    this._players[0].addEventListener('playerReady', () => {
      this.dataset.status = 'ready';
    }, { once: true });

    const playerResizeHandler = () => {
      this._maybeOverrideJustifyCenter();
      this._updateRowNavigationState();
    };

    const playerMinimizeHandler = (e) => {
      const url = e?.detail?.url;
      if (this._attr.navigationMode === NAVIGATION_MODE.FAB) {
        // Minimize & navigate with pagefacade
        this.minimizeAboveURL(url);
      } else if (this._attr.navigationMode === NAVIGATION_MODE.FAB_MANUAL) {
        // Minimize as FAB but keep the original playlist
        // Dispatch some event to let the page know that some navigation has been requested
        if (url !== window.location.href) {
          // Event for host environment the perform the navigation
          this._emit('bam-playlist:navigate-behind-to', { url });
        }

        // TODO Show FAB (but without any surfbehind iframe)
      } else if (this._attr.navigationMode === NAVIGATION_MODE.NONE) {
        // Do nothing when navigation mode is set to "none"
        // It should not happen
        console.warn('Received playerMinimize event but navigation mode is set to "none", so ignoring the event'); 
      }
    };

    this._players.map((bamPlayer) => {
      bamPlayer.addEventListener('playerResize', playerResizeHandler);
      bamPlayer.addEventListener('playerMinimize', playerMinimizeHandler);
    });

    // Set up autoplay in cascade mode, meaning we kick off the first player in the series,
    // then after a while we start the next one, and so on.
    this._startAutoplayCascade();

    // React to changes in the viewport size
    this._bumpVhCSSVariable();
    this._maybeOverrideJustifyCenter();
    window.addEventListener('resize', () => {
      this._bumpVhCSSVariable();
      this._maybeOverrideJustifyCenter();
      this._updateRowNavigationState();
      this._determineScrollDirection();
      this._scrollToPlayer(this._focusedPlayer, true, false);
    });

    // Expose the following methods in the global scope for the user to control the carousel
    this.scrollForward = this._onNext.bind(this);
    this.scrollBackward = this._onPrevious.bind(this);
    this.expand = () => {
      const player = this._players[0];
      if (!player) return;
      if (player._isReady) {
        player.click();
      } else {
        // Avoid setting up multiple listeners
        if (this._pendingExpand) return;
        this._pendingExpand = true;
        // Wait for the player to be ready before attempting to expand
        player.addEventListener('playerReady', () => {
          this._pendingExpand = false;
          player.click();
        });
      }
    };

    if (startFocused) {
      this._players[0]?.addEventListener('playerReady', () => {
        this._players[0]?.click();
      });
    }

    this._updateViewState();

    if (debugMode) {
      emitDebugMessage(this._debugId, 'bam-playlist:setupComplete', { setupId, attributes: this._attr });
    }
  }

  _addFabPreviewCloseButton(wrapper) {
    if (this.querySelector('button.fab-preview-close-button')) {
      // The FAB preview close button already exists, so we do not need to create it again.
      return;
    }
    const fabPreviewCloseButton = document.createElement('button');
    fabPreviewCloseButton.classList.add('fab-preview-close-button');

    const fabPreviewCloseButtonTextContainer = document.createElement('span');
    fabPreviewCloseButtonTextContainer.textContent = '✕';
    fabPreviewCloseButtonTextContainer.setAttribute('aria-hidden', 'true');
    fabPreviewCloseButton.appendChild(fabPreviewCloseButtonTextContainer);
    fabPreviewCloseButton.addEventListener('click', () => this._closeFAB());
    wrapper.appendChild(fabPreviewCloseButton);
  }

  _removeFabPreviewCloseButton() {
    const fabPreviewCloseButton = this.querySelector('button.fab-preview-close-button');
    if (fabPreviewCloseButton) {
      fabPreviewCloseButton.remove();
    }
  }

  _hideAllFabPlaylists() {
    this._fabsStyleInfo = [];

    const bamPlaylists = document.querySelectorAll('bam-playlist');
    for (const bamPlaylist of bamPlaylists) {
      if (
        bamPlaylist === this
        || bamPlaylist?._attr?.mode !== 'fab'
      ) continue;
      const inlineStyle = bamPlaylist.style;
      const fabStyleInfo = {
        display: inlineStyle.display,
      };

      this._fabsStyleInfo.push([bamPlaylist, fabStyleInfo]);
      bamPlaylist.style.setProperty('display', 'none', 'important');
    }
  }

  _unhideAllFabPlaylists() {
    for (const [bamPlaylist, fabStyleInfo] of this._fabsStyleInfo || []) {
      bamPlaylist.style.display = fabStyleInfo.display;
    }
  }

  _overrideAncestorsStyles() {
    if (!document.getElementById('bam-ancestor-override-style')) {
      const style = document.createElement('style');
      style.id = 'bam-ancestor-override-style';
      style.textContent = '.bam-no-containing-block{transform:none!important;will-change:auto!important;}';
      document.head.appendChild(style);
    }
    let el = this;
    this.ancestorStyleInfo = [];
    const ancestorComputedStyles = new Map();
    const ancestorScrollPositions = new Map();

    // override the styles in two steps - first get scroll positions for all elements,
    // then override the styling
    while (el !== document) {
      const computedStyle = window.getComputedStyle(el);
      ancestorComputedStyles.set(el, computedStyle);
      const scrollPosition = {};
      if (el.scrollLeft) {
        scrollPosition.scrollLeft = el.scrollLeft;
      }
      if (el.scrollTop) {
        scrollPosition.scrollTop = el.scrollTop;
      }
      ancestorScrollPositions.set(el, scrollPosition);
      el = el.parentNode;
    }
    el = this;
    while (el !== document) {
      const inlineStyle = el.style;
      const computedStyle = ancestorComputedStyles.get(el);
      const ancestorStyleInfo = {
        position: inlineStyle.position,
        zIndex: inlineStyle.zIndex,
        transform: inlineStyle.transform,
        overflowX: inlineStyle.overflowX,
        overflowY: inlineStyle.overflowY,
        display: inlineStyle.display,
        scrollbarGutter: inlineStyle.scrollbarGutter,
        ...ancestorScrollPositions.get(el),
      };

      this.ancestorStyleInfo.push([el, ancestorStyleInfo]);

      if (el.classList) {
        el.classList.add('bam-no-containing-block');
      }
      if (computedStyle.position === 'static') {
        el.style.position = 'relative';
      }
      if (computedStyle.transform !== 'none') {
        el.style.setProperty('transform', 'none', 'important');
      }
      if (computedStyle.overflowX !== 'visible') {
        el.style.overflowX = 'visible';
      }
      if (computedStyle.overflowY !== 'visible') {
        el.style.overflowY = 'visible';
      }
      if (computedStyle.display !== 'block' && computedStyle.display !== '') {
        el.style.display = 'block';
      }
      if (computedStyle.animation !== 'none') {
        el.style.animation = 'none';
      }
      if (computedStyle.opacity !== '1') {
        el.style.opacity = '1';
      }
      if (computedStyle.scrollbarGutter !== 'auto') {
        el.style.scrollbarGutter = 'auto';
      }

      el.style.setProperty('z-index', '2147483647', 'important');
      el = el.parentNode;
    }
  }

  async _revertAncestorsStyles() {
    // reset the styles in two steps - first reset styling for all elements,
    // then reset the scroll positions
    const reversedAncestors = [...this.ancestorStyleInfo].reverse();
    reversedAncestors.forEach(([el, inlineStyle]) => {
      if (el.classList) {
        el.classList.remove('bam-no-containing-block');
      }
      const currentInlineStyle = el.style;
      [
        'position',
        'zIndex',
        'transform',
        'overflowX',
        'overflowY',
        'display',
        'animation',
        'opacity',
        'scrollbarGutter',
      ].forEach((propertyName) => {
        resetStyleProperty(currentInlineStyle, inlineStyle, propertyName);
      });
    });
    // when resetting scroll positions, we need to wait for a frame between
    // setting the scroll position of each element, otherwise the positions
    // were sometimes not set correctly
    for (const [el, inlineStyle] of reversedAncestors) {
      await new Promise((resolve) => {
        if (inlineStyle.hasOwnProperty('scrollLeft') || inlineStyle.hasOwnProperty('scrollTop')) {
          window.requestAnimationFrame(() => {
            if (inlineStyle.hasOwnProperty('scrollLeft')) {
              el.scrollLeft = inlineStyle.scrollLeft;
            }
            if (inlineStyle.hasOwnProperty('scrollTop')) {
              el.scrollTop = inlineStyle.scrollTop;
            }
            resolve();
          });
        } else {
          resolve();
        }
      });
    }
  }

  _createBamPlayer = ({
    eventId,
    focusMode,
    autoplay,
    isSingleVideo,
    playerFit,
    mediaAssets,
    landscapeInPortrait,
    showPosterWhenPaused,
    startMuted,
    cookieDomain,
    cookieTTLDays,
    softLimit,
    productAction,
    closedCaptions,
    closedCaptionsLocaleHint,
    inPlaylistIndex,
    playlistLength,
    navigationMode,
  }) => {
    const bamPlayer = document.createElement('bam-player');
    bamPlayer._isDesktop = this._isDesktop; // In appearance preview mode we may be forcing desktop mode

    // Provide the index in the playlist to the player
    bamPlayer._playlistIndex = this.autoIndexId;
    bamPlayer._inPlaylistIndex = inPlaylistIndex;
    bamPlayer._playlistLength = playlistLength;
    bamPlayer._playerConfig = this._remotePlayerConfig;
    bamPlayer._placementMetadata = this._placementMetadata;

    if (this._previewOrgId) {
      bamPlayer._previewOrgId = this._previewOrgId;
    }

    if (mediaAssets) {
      bamPlayer.setMediaAssets(mediaAssets);
    }

    if (this._distributionPageId) {
      bamPlayer.setAttribute('distribution-page-id', this._distributionPageId);
    }

    if (focusMode) {
      bamPlayer.setAttribute('focus-mode', focusMode);
    }

    if (startMuted) {
      bamPlayer.setAttribute('start-muted', startMuted);
    }

    if (landscapeInPortrait) {
      bamPlayer.setAttribute('landscape-in-portrait', landscapeInPortrait);
    }

    if (showPosterWhenPaused) {
      bamPlayer.setAttribute('show-poster-when-paused', showPosterWhenPaused);
    }

    if (cookieDomain) {
      bamPlayer.setAttribute('cookie-domain', cookieDomain);
    }

    if (cookieTTLDays) {
      bamPlayer.setAttribute('cookie-ttl-days', cookieTTLDays);
    }

    if (productAction) {
      bamPlayer.setAttribute('product-action', productAction);
    }

    if (closedCaptions) {
      bamPlayer.setAttribute('closed-captions', closedCaptions);
    }

    if (closedCaptionsLocaleHint) {
      bamPlayer.setAttribute('closed-captions-locale-hint', closedCaptionsLocaleHint);
    }

    const collectMetrics = this.getAttribute('collect-metrics');
    if (collectMetrics !== null) {
      bamPlayer.setAttribute('collect-metrics', collectMetrics);
    }

    if (navigationMode) {
      bamPlayer.setAttribute('navigation-mode', navigationMode);
    }

    if (this._distributionContainerId) {
      bamPlayer.setAttribute('distribution-container-id', this._distributionContainerId);
    }

    if (this._playlistId) {
      bamPlayer.setAttribute('playlist-id', this._playlistId);
    }

    if (this._appearanceGroupId) {
      bamPlayer.setAttribute('appearance-group-id', this._appearanceGroupId);
    }

    if (this._appearanceId) {
      bamPlayer.setAttribute('appearance-id', this._appearanceId);
    }

    if (this._placementId) {
      bamPlayer.setAttribute('placement-id', this._placementId);
    }

    if (this._themeId) {
      bamPlayer.setAttribute('theme-id', this._themeId);
    }

    // Pass player settings to the player
    bamPlayer.setAttribute('settings', getSettingsString(this._playerSettings));

    if (this._videosDataSource === 'vod') {
      bamPlayer.setAttribute('video-id', eventId);
    } else {
      bamPlayer.setAttribute('event-id', eventId);
    }

    bamPlayer.setAttribute('player-fit', playerFit);

    bamPlayer.addEventListener('muted', ()=> {
      this._isMuted = true;

      this._players.forEach((player) => player._saveGlobalState({ muted: true }));
    });

    bamPlayer.addEventListener('unmuted', ()=> {
      this._isMuted = false;

      this._players.forEach((player) => player._saveGlobalState({ muted: false }));
    });

    bamPlayer.addEventListener('focus-web-component-outside-iframe', () => {
      this.shadow.querySelector('[data-default-focus]')?.focus();
    });

    bamPlayer.addEventListener('top-ui-element-shown', () => {
      this.hideCloseButton();
    });

    bamPlayer.addEventListener('top-ui-element-hidden', () => {
      this.showCloseButton();
    });

    bamPlayer.addEventListener('clickproduct', (e) => {
      this._emit('clickproduct', {
        ...e.detail,
        containerId: this._attr.containerId,
      });
    });

    bamPlayer.addEventListener('customizations-loaded', (e) => {
      if (!this._customizations) {
        this._customizations = e.detail.customizations;
        this._applyCustomizations();
      }
    });

    if (autoplay === 'hover') {
      bamPlayer.setAttribute('autoplay', 'hover');
    } else if (autoplay === 'cascade') {
      // We'll handle autoplay ourselves
      const onMouseEvent = (event) => {
        if (this._isExpanded) return;
        if (event.type === 'mouseenter') {
          this._stopAutoplayCascade();
          bamPlayer.play();
        } else {
          this._startAutoplayCascade(bamPlayer);
        }
      };
      // Only necessary when there are more than one player
      if (!isSingleVideo) {
        bamPlayer.addEventListener('mouseenter', onMouseEvent);
        bamPlayer.addEventListener('mouseleave', onMouseEvent);
      }
      bamPlayer.addEventListener('focusin', this._stopAutoplayCascade.bind(this));
      // Enable looping to ensure players will continue playing when the cascade reaches them again
      bamPlayer.setAttribute('loop', true);
    } else if (autoplay === 'none') {
      // do nothing, autoplay is disabled
    }

    const detailEventIdKey = this._videosDataSource === 'vod' ? 'videoId' : 'eventId';
    const triggerClickPlayerEvent = () => {
      const detail = {
        [detailEventIdKey]: eventId,
      };
      this._emit('clickplayer', detail);
    };

    // Playback progress of each player, so a page can tell a preview from a
    // video the user opened and watched.
    bamPlayer.addEventListener('timeupdate', () => {
      this._emit('timeupdate', {
        [detailEventIdKey]: eventId,
        currentTime: bamPlayer._currentTime,
        expanded: !!this._isExpanded,
      });
    });

    // Set up everything needed for the carousel mode
    if (focusMode === FOCUS_MODE.CAROUSEL) {
      bamPlayer.setAttribute('standalone', 'false'); // We'll handle the expansion ourselves
      bamPlayer.addEventListener('close', () => this._contract());

      if (!isSingleVideo) {
        bamPlayer.addEventListener('show-product', () => {
          if (this._isExpanded && this._scrollDirection === 'vertical') {
            this._blockScroll();
          }
        });

        bamPlayer.addEventListener('hide-product', () => {
          this._unblockScroll();
        });
      }

      // Auto-advance to the next player when the current one ends
      bamPlayer.addEventListener('ended', () => {
        if (this._focusedPlayer === bamPlayer && !this._focusedPlayer.hasImportantUIShown()) {
          this._next();
        }
      });

      const enterCarouselMode = () => {
        const wasExpanded = this._isExpanded;
        // With expand-from-start, the collapsed playlist opens on its first
        // video, and every video starts over, whichever tile was clicked and
        // wherever its preview was. The preview that was playing stops, as
        // only the focused player plays.
        const startFromFirst = !wasExpanded && this._attr.expandFromStart;
        const targetPlayer = startFromFirst ? this._players[0] : bamPlayer;
        if (startFromFirst) {
          this._players.forEach((player) => {
            if (player !== targetPlayer) player.pause();
            if (player._currentTime > 0) player.setCurrentTime(0);
          });
        }
        this._expand();

        if (!wasExpanded && this._players.indexOf(targetPlayer) === 0) {
          // For the case when no scrolling is needed - clicking on the first player
          // in the initial position
          this._onScroll();
        }

        this._scrollToPlayer(targetPlayer, wasExpanded, false);
      };

      // Clicking on a player should enter carousel mode and focus on the player that was clicked
      bamPlayer.addEventListener('click', () => {
        enterCarouselMode();
        triggerClickPlayerEvent();
      });

      bamPlayer.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          enterCarouselMode();
          triggerClickPlayerEvent();

          if (bamPlayer?._isExpandedInPlaylist) {
            bamPlayer.focusFirstInteractiveElementInsideIframe();
          }
        }
      });
    }

    if (focusMode === FOCUS_MODE.SINGLE) {
      bamPlayer.addEventListener('click', () => {
        bamPlayer._onClick();
        this._saveFocus();
        this._expanded = true;
        this._updateViewState();
        this._wrapper.classList.add('is-expanded-single');
        this._updateStylesheet(bamPlayer);
        bamPlayer.updateScaleMode();
        this._overrideAncestorsStyles();
        this._hideAllFabPlaylists();
        triggerClickPlayerEvent();
      });
      bamPlayer.addEventListener('close', () => {
        this._expanded = false;
        this._updateViewState();
        this._wrapper.classList.remove('is-expanded-single');
        this._updateStylesheet(bamPlayer);
        this._revertAncestorsStyles();
        this._unhideAllFabPlaylists();
        bamPlayer.updateScaleMode();
        this._restoreFocus();
      });
    }

    if (focusMode === FOCUS_MODE.PLAYPAUSE) {
      bamPlayer.setAttribute('standalone', 'false');

      const togglePlayingState = () => {
        this._stopAutoplayCascade();
        if (bamPlayer._isPlaying) {
          bamPlayer.pause();
        } else {
          bamPlayer.play();
        }
      };

      bamPlayer.addEventListener('click', () => {
        if (this._isExpanded) {
          this._scrollToPlayer(bamPlayer, true, false);
          triggerClickPlayerEvent();
          return;
        }
        togglePlayingState();
        triggerClickPlayerEvent();
      });

      bamPlayer.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          if (this._isExpanded) {
            this._scrollToPlayer(bamPlayer, true, false);
            triggerClickPlayerEvent();
            return;
          }
          togglePlayingState();
          triggerClickPlayerEvent();
        }
      });

      bamPlayer.addEventListener('expand-button-click', () => {
        const wasExpanded = this._isExpanded;
        this._expand();

        if (!wasExpanded && inPlaylistIndex === 0) {
          this._onScroll();
        }

        this._scrollToPlayer(bamPlayer, wasExpanded, false);
        bamPlayer._activatePlayerUI();
      });
    }

    if (focusMode === FOCUS_MODE.NONE) {
      bamPlayer.setAttribute('standalone', 'false');

      const onClick = () => {
        if (this._isExpanded) {
          this._scrollToPlayer(bamPlayer, true, false);
          triggerClickPlayerEvent();
          return;
        }
        triggerClickPlayerEvent();
      };

      const onKeyDown = (e) => {
        if (e.key === 'Enter') {
          if (this._isExpanded) {
            this._scrollToPlayer(bamPlayer, true, false);
            triggerClickPlayerEvent();
            return;
          }
          triggerClickPlayerEvent();
        }
      };

      bamPlayer.addEventListener('click', onClick);
      bamPlayer.addEventListener('keydown', onKeyDown);

      bamPlayer.addEventListener('expand-button-click', () => {
        const wasExpanded = this._isExpanded;
        this._expand();

        if (!wasExpanded && inPlaylistIndex === 0) {
          this._onScroll();
        }

        this._scrollToPlayer(bamPlayer, wasExpanded, false);
        bamPlayer._activatePlayerUI();
      });
    }

    // Set player loop setting
    this._remotePlayerConfig?.loop && bamPlayer.setAttribute('loop', 'true');

    // Pass show-product setting to the player
    this._remotePlayerConfig?.showProducts !== undefined
      ? bamPlayer.setAttribute('show-products', this._remotePlayerConfig.showProducts)
      : bamPlayer.removeAttribute('show-products');

    this._remotePlayerConfig?.showActions !== undefined
      ? bamPlayer.setAttribute('show-actions', this._remotePlayerConfig.showActions)
      : bamPlayer.removeAttribute('show-actions');

    if (softLimit > 0 && inPlaylistIndex + 1> softLimit) {
      // If we use a soft-limit to only render a subset of the players initially (in contracted mode)
      // we can defer loading of the players that are not initially visible to conserve resources.
      bamPlayer.setAttribute('defer-load', 'true');
    }

    // Wrap each bam-player element in a div
    const playerWrapper = document.createElement('li');
    playerWrapper.classList.add('player-wrapper');
    playerWrapper.appendChild(bamPlayer);

    // Use a a DocumentFragment and a shared requestAnimationFrame to batch DOM
    // updates to avoid layout thrashing and keep the main thread more responsive.
    if (!this._pendingPlayerFragment) this._pendingPlayerFragment = document.createDocumentFragment();
    this._pendingPlayerFragment.appendChild(playerWrapper);
    if (!window.__bamPlayerAppendQueue) window.__bamPlayerAppendQueue = [];
    window.__bamPlayerAppendQueue.push(this);
    if (!window.__bamPendingAnimationFrame) {
      window.__bamPendingAnimationFrame = true;
      window.requestAnimationFrame(() => {
        // Go through each playlist in the queue and append all pending players in each playlist
        window.__bamPlayerAppendQueue.forEach((_playlist) => {
          if (!_playlist._pendingPlayerFragment) return;
          _playlist._playerContainer.appendChild(_playlist._pendingPlayerFragment);
          _playlist._pendingPlayerFragment = null; // Reset for next time
          _playlist._updateRowNavigationState();
        });
        window.__bamPendingAnimationFrame = false;
        window.__bamPlayerAppendQueue = [];
      });
    }

    if (this._isExpanded) {
      bamPlayer._expanded = true;
    }

    return bamPlayer;
  };

  _loadNextPaginatedVideos = async () => {
    // If no next page available, let's do nothing obviously
    if (!this._hasNextPage) return;
    if (this._isLoadingMore) return;
    this._isLoadingMore = true;

    const {
      focusMode,
      autoplay,
      limit,
      softLimit,
      playerFit,
      landscapeInPortrait,
      showPosterWhenPaused,
      startMuted,
      cookieDomain,
      cookieTTLDays,
      productAction,
      closedCaptions,
      closedCaptionsLocaleHint,
      navigationMode,
    } = this._attr;
    // check current amount of available players in player container
    const currentAmountPlayers = Array.from(this._playerContainer.childNodes).filter((n) => n.classList.contains('player-wrapper')).length;
    // if the configured limit is reached, do nothing
    if (currentAmountPlayers >= limit) {
      this._isLoadingMore = false;
      return;
    }

    let playlist = [];
    let playlistMetadata = [];
    try {
      const { playlist: actualPlaylist, page, total, playlistMetadata: actualPlaylistMetadata } = await loadPlaylist({
        ...this._attr,
        offset: this._page * this._pageSize,
      });

      playlist = actualPlaylist;
      playlistMetadata = actualPlaylistMetadata;
      this._hasNextPage = total > page * this._pageSize;
      this._page = page;
    } catch (err) {
      this._printError('Failed to load playlist with pagination', { err, page: this._page, pageSize: this._pageSize });
    }

    // Create a bam-player for each show in the playlist until we've reached the configured limit
    this._players = this._players.concat(...playlist.slice(0, limit - currentAmountPlayers).map((eventId, index) => this._createBamPlayer({
      eventId,
      focusMode,
      autoplay,
      isSingleVideo: playlist.length === 1,
      playerFit,
      mediaAssets: playlistMetadata?.find(videoMetadata => videoMetadata.id === eventId)?.mediaAssets,
      landscapeInPortrait,
      showPosterWhenPaused,
      startMuted: startMuted && this._isMuted === undefined ? true : this._isMuted,
      cookieDomain,
      cookieTTLDays,
      productAction,
      closedCaptions,
      closedCaptionsLocaleHint,
      inPlaylistIndex: currentAmountPlayers + index,
      playlistLength: this._total ? this._total - 1 : 0,
      navigationMode,
    })));

    if (!this._hasNextPage || this._players.length >= Math.min(limit, softLimit)) {
      // No more pagination or reached player limit, ditch load more button
      this._loadMoreButton?.remove();
    }

    this._isLoadingMore = false;
  };

  _blockScroll() {
    if (this._playerContainer.style.overflow !== 'hidden') {
      this._playerContainer.style.overflow = 'hidden';
    }
  }

  _unblockScroll() {
    if (this._playerContainer.style.overflow === 'hidden') {
      this._playerContainer.style.overflow = '';
    }
  }

  set _expanded(value) {
    this._isExpanded = value;

    this._players?.forEach((player) => {
      player._expanded = value;
    });
  }

  _updateViewState() {
    const mode = this._attr?.mode;
    if (this._isExpanded) {
      this.dataset.viewState = VIEW_STATE.EXPANDED;
    } else if (mode === 'fab') {
      this.dataset.viewState = VIEW_STATE.FAB;
    } else {
      this.dataset.viewState = VIEW_STATE.DEFAULT;
    }
  }

  _expand() {
    if (this._isExpanded) return;

    if (PageFacade?.isOpenedByPageFacade()) {
      // Send a message to the parent window that we've expanded so it can
      // kill any playlist it may be running in FAB mode.
      window.parent.postMessage(JSON.stringify({
        eventName: 'bam-playlist:expand-in-subpage',
      }));
    }

    // If we're using a priority queue to preload players, let's pause it while we're expanded
    // to avoid unnecessary network activity.
    if (window.__bamPlayerPriorityQueue) {
      window.__bamPlayerPriorityQueue.pause();
    }

    this._saveFocus();
    this._expanded = true;
    this._updateViewState();

    if (this._attr.fullscreen && !this._isDesktop) {
      enterFullscreen(this);
    }

    this._bumpVhCSSVariable(); // For good measure...
    this._overrideAncestorsStyles();
    this._hideAllFabPlaylists();

    const wrapper = this._wrapper;
    wrapper.classList.add('is-expanded');

    this._players.forEach((player) => {
      player.updateScaleMode();
    });

    // Keep a copy of the styles currently set on the body
    this._bodyStylesCopy = copyStyleFromEl(document.body);

    // Set the body to a fixed position to prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.body.style.padding = '0';
    document.body.style.margin = '0';

    // Stop autoplay cascade if it's running
    this._stopAutoplayCascade();
  }

  _closeFAB() {
    if (this._attr.mode !== 'fab') return;
    if (this._isExpanded) return;

    if (PageFacade?.isOpen()) {
      // Return the original playlist to its state before we expanded.
      this._attrOverrides = {};
      this._removeFabPreviewCloseButton();
      this._updateStylesheet();
      this._updateViewState();
      // We never close PageFacade now
      // PageFacade?.close();
    } else {
      // For good measure, let's stop the autoplay cascade
      this._stopAutoplayCascade();
      // Remove the bam-playlist from the DOM
      this.remove();
    }
  }

  // REMARK: This function could be called from an outside actor in order to contract the expanded playlist
  // and want to return to the initial state, which in turn also means cleaning up any added or modified z-indexes
  // on the ancestors of the bam-playlist component which is added/modified to make sure the carousel or single mode
  // player is displayed properly on the site. The 'LIVE' FAB component is an example of a component that could
  // call this function.
  _contract({ startAutoplayCascade = true } = {}) {
    if (!this._isExpanded) return;
    this._expanded = false;
    this._updateViewState();

    // If we're using a priority queue to preload players, let's resume it now that we're contracted
    if (window.__bamPlayerPriorityQueue) {
      window.__bamPlayerPriorityQueue.resume();
    }

    if (isFullscreen()) {
      exitFullscreen();
    }

    this._players.forEach((player) => {
      player.setScaleMode('aspectFill');
      // Let's force unload all players to prevent any inconsistencies
      player.pause();
      player._unloadPlayerUI();
    });

    this._wrapper.classList.remove('is-expanded');

    const focusedPlayerIndex = this._players.indexOf(this._focusedPlayer);
    const focusedPlayer = !this._attr.softLimit || focusedPlayerIndex >= this._attr.softLimit
      ? null // If we have a soft limit and the focused player is outside of the limit, we start from the beginning
      : this._focusedPlayer;

    this._setFocusedPlayer(null);

    // Reset the styles on the body to what they were before we expanded
    applyStyleToEl(document.body, this._bodyStylesCopy);
    this._revertAncestorsStyles();
    this._unhideAllFabPlaylists();

    // Restore focus before the cascade restarts: focusing a player emits
    // focusin, which stops the cascade. A FAB shows one player at a time and
    // the cascade scrolls to the one it plays, so don't scroll to the focused
    // player, which may be another one.
    this._restoreFocus({ scroll: this._attr.mode !== 'fab' });

    // Restart autoplay cascade if it should be running. Let's start from the
    // player that was focused before we contracted and forcefully scroll to its position,
    // in the case of mode = fab because playing it potentionally out of view sounds weird.
    if (startAutoplayCascade) {
      const shouldForceScroll = this._attr.mode === 'fab';
      this._startAutoplayCascade(focusedPlayer, shouldForceScroll);
    }

    // In case any player have shown the product view, but the player did not close it prior to the contract,
    // then we'd never receive the 'hide-product' event which would have unblocked the scroll.
    // So we make sure to unblock the scroll here.
    this._unblockScroll();

    this._emit('close');
  }

  minimizeAboveURL(url = window.location.href) {
    // Save the export context to be able to pass it to a PageFacade if needed
    // before we update focusedPlayer.
    const exportContext = this._exportContext();
    exportContext.attr.mode = 'fab';
    exportContext.attr.autoplay = 'cascade';

    const prevFocusedPlayer = this._focusedPlayer;

    const contractToFab = () => {
      if (this._isExpanded) {
        this._contract({ startAutoplayCascade: false });
      }

      if (this._attr.mode !== 'fab') {
        this._addFabPreviewCloseButton(this._wrapper);
        this._attrOverrides = {
          ...this._attrOverrides,
          mode: 'fab',
          autoplay: 'cascade',
        };
        this._updateStylesheet();
        this._updateViewState();
      }

      setTimeout(() => {
        this._startAutoplayCascade(prevFocusedPlayer, true);
      }, 50);
    };

    if (!PageFacade?.isOpen()) {
      // Switch to FAB mode
      contractToFab();

      // Open a PageFacade with the given URL and make sure we're above it
      const pf = PageFacade?.open({ debug: debugMode, zIndex: MAX_INT - 1, url });

      // Make sure we're not hidden by PageFacade by adding the PageFacade.ignoreCSSClass to this
      // playlist and all parent elemets up to the body.
      this.classList.add(PageFacade.ignoreCSSClass);
      const ancestors = [];
      let el = this.parentNode;
      while (el && el !== document.body) {
        ancestors.push(el);
        el = el.parentNode;
      }
      ancestors.forEach((ancestor) => {
        ancestor.classList.add(PageFacade.ignoreCSSClass);
      });

      this._isPlaylistAbovePageFacade = true;
      pf.addEventListener('close', () => {
        this.classList.remove(PageFacade.ignoreCSSClass);
        const ancestors = [];
        let el = this.parentNode;
        while (el && el !== document.body) {
          ancestors.push(el);
          el = el.parentNode;
        }
        ancestors.forEach((ancestor) => {
          ancestor.classList.remove(PageFacade.ignoreCSSClass);
        });

        this._isPlaylistAbovePageFacade = false;
        this._updateStylesheet();
      }, { once: true });
    } else if (PageFacade?.isOpenedByPageFacade()) {
      this._contract();
      window.parent.postMessage(JSON.stringify({
        eventName: 'bam-playlist:contract-in-subpage',
        data: {
          context: exportContext,
        },
      }));
    } else {
      contractToFab();
      PageFacade?.navigateTo(url);
    }
  }

  _getPlayerById(id) {
    return this._players.find((player) => player._id === id);
  }

  _setFocusedPlayer(player) {
    if (this._focusedPlayer && this._focusedPlayer !== player) {
      this._focusedPlayer.pause();

      player?.setCurrentTime(0); // Always start from 0

      const lastFocusedPlayerId = this._focusedPlayer._id;
      clearTimeout(this._playerVisibilityMap[lastFocusedPlayerId]);
      this._playerVisibilityMap[lastFocusedPlayerId] = setTimeout(() => {
        if (lastFocusedPlayerId !== this._focusedPlayer?._id) {
          this._getPlayerById(lastFocusedPlayerId)?._unloadPlayerUI();
        }
      }, UNLOAD_PLAYER_UI_DELAY_MS);

      this._focusedPlayer.parentNode.classList.remove('focused');

      // Reset the opacity of all players (no more custom fade based on visibility)
      this._players.forEach(p => {
        p.parentElement.style.opacity = '';
      });
    }

    if (!player) {
      this._focusedPlayer = null;
      return;
    }

    this.showCloseButton();

    this._focusedPlayer = player;
    if (this._focusedPlayer._isPlayerUILoaded) {
      this._focusedPlayer.play();
      this._isMuted ? this._focusedPlayer.mute() : this._focusedPlayer.unmute();
    } else {
      this._focusedPlayer._loadPlayerUI();
    }
    this._focusedPlayer.parentNode.classList.add('focused');

    // List players in the order they appear in the DOM (respecting any CSS order property)
    const sortedPlayers = this._sortedPlayersRespectingCSSOrder();
    const index = sortedPlayers.indexOf(player);

    if (this._isExpanded) {
      // Find two players before and after the focused player and call load
      // on them to make sure they are ready to play (in case they were deferred)
      const previousPlayers = sortedPlayers.slice(index - 2, index);
      const nextPlayers = sortedPlayers.slice(index + 1, index + 3);
      [...previousPlayers, ...nextPlayers].forEach((player) => {
        player?.load();

        // Piggyback on this logic to reset the playhead on these players to 0
        // to avoid any weirdness when the user scrolls back to them.
        // Delay a bit to avoid too much happening at once.
        setTimeout(() => {
          if (player !== this._focusedPlayer) {
            player.setCurrentTime(0);
          }
        }, 2000);
      });
    }

    // If there are only a couple of videos left in the carousel, automatically
    // load the next paginated videos (if any).
    if (sortedPlayers.slice(index + 1).length < 3) {
      this._loadNextPaginatedVideos();
    }
  }

  _sortedPlayersRespectingCSSOrder() {
    return this._players.slice().sort((a, b) => {
      const orderA = parseInt(getComputedStyle(a.parentElement).getPropertyValue('order')) || 0;
      const orderB = parseInt(getComputedStyle(b.parentElement).getPropertyValue('order')) || 0;
      return orderA - orderB;
    });
  }

  _startAutoplayCascade(startFromPlayer = null, forceScroll) {
    if (!this._players?.length && !startFromPlayer) return;
    if (this._attr.autoplay !== 'cascade') return;
    if (this._isCascadeRunning) return;

    // Stop all players and reset the autoplay attribute to start from scratch
    this._players.forEach((player) => {
      player.pause();
      player.setAttribute('autoplay', 'hover');
    });

    this._isCascadeRunning = true;
    this._curCascadePlayer = null;
    const moveToNextPlayer = (shouldForceScroll) => {
      // Either start from a given player or choose next one in the series
      let nextPlayer;

      const scrollPlayerIntoView = () => {
        // Scroll the container to reveal the next player
        scrollToElement(this._playerContainer, nextPlayer, this._attr.mode === 'fab', shouldForceScroll);
      };

      const softLimit = this._attr.softLimit || Infinity;

      if (startFromPlayer) {
        nextPlayer = startFromPlayer;
        startFromPlayer = null;
        if (this._attr.mode === 'fab') scrollPlayerIntoView();
      } else {
        nextPlayer = this._players.slice(0, softLimit)[this._players.indexOf(this._curCascadePlayer) + 1];
        if (!nextPlayer) {
          // Start from the beginning
          // TODO: consider making this a setting
          nextPlayer = this._players[0];
        }
        // getVisibleArea() measures against the window, but a FAB clips its
        // players to one tile: a player beside the tile counts as fully visible
        // there. So in FAB mode, always bring the next player into the tile.
        if (this._attr.mode === 'fab' || nextPlayer.getVisibleArea() < 1) {
          scrollPlayerIntoView();
        }
      }

      // TODO: If we for some reason abort the cascade before the player has started playing
      // we should probably remove the event listener to avoid possible weird behavior.
      const onPlaying = () => {
        nextPlayer.removeEventListener('playing', onPlaying);
        // Stop the previous player and promote the next one to the current player
        // If it's the same player, we don't want to setAttribute('autoplay', 'hover')
        // because it will cause the player to stop playing if it is out of screen
        // and then, on the mobiles, it won't resume playing because only limited number of mobiles
        // support hover event.
        if (this._curCascadePlayer !== nextPlayer) {
          this._curCascadePlayer?.setAttribute('autoplay', 'hover');
          this._curCascadePlayer = nextPlayer;
        }

        if (!this._isCascadeRunning) {
          nextPlayer?.setAttribute('autoplay', 'hover');
          return;
        }

        // Move on to the next player after a while. If there's only one player, we'll just
        // start the same player from the beginning every AUTOPLAY_CASCADE_PLAY_DURATION_MS.
        this._cascadePlayerTimeoutHandle = setTimeout(() => moveToNextPlayer(false), AUTOPLAY_CASCADE_PLAY_DURATION_MS);
      };
      if (nextPlayer._isPlaying) {
        onPlaying();
      } else {
        nextPlayer.addEventListener('playing', onPlaying);
      }

      // Start playback on the next player
      nextPlayer.setAttribute('autoplay', 'visible');
    };
    moveToNextPlayer(forceScroll);
  }

  _stopAutoplayCascade() {
    clearTimeout(this._cascadePlayerTimeoutHandle);
    if (!this._isCascadeRunning) return;
    this._isCascadeRunning = false;

    this._players?.forEach((player) => {
      player.pause();
      player.setAttribute('autoplay', 'hover');
    });

  }

  _determineScrollDirection() {
    // This should hopefully match our landscape/portrait media queries
    const prevScrollDir = this._scrollDirection;
    this._scrollDirection = window.innerHeight > window.innerWidth ? 'vertical' : 'horizontal';
    if (prevScrollDir !== this._scrollDirection) {
      this._scrollToPlayer(this._focusedPlayer, false, false);
    }
  }

  _next() {
    if (this._isExpanded && this._players.length > 1) {
      const nextPlayer = this._players[this._players.indexOf(this._focusedPlayer) + 1] || this._players[0];
      this._scrollToPlayer(nextPlayer);

      // check if we're on the last player, if so, let's load more
      if (this._players.indexOf(nextPlayer) === this._players.length - 1) {
        this._loadNextPaginatedVideos();
      }
    }
  }

  _prev() {
    if (this._isExpanded && this._players.length > 1) {
      const prevPlayer = this._players[this._players.indexOf(this._focusedPlayer) - 1] || this._players[this._players.length - 1];

      this._scrollToPlayer(prevPlayer);
    }
  }

  _scrollToPlayer(player, animated = true, hideCurrentFocusedInstantly = true) {
    if (!player) return;

    if (hideCurrentFocusedInstantly && this._isExpanded && this._focusedPlayer?.parentElement) {
      this._focusedPlayer.parentElement.classList.remove('focused');
      this._focusedPlayer.parentElement.style.opacity = '';
    }

    // Unblock scroll if it was blocked by the player opening a product view but never closing it
    // before moving on to the next player, which can happen if the user clicks the next player
    // in the carousel while the product view is open.
    this._unblockScroll();

    player.scrollIntoView({
      ...animated && { behavior: 'smooth' },
      block: 'center',
      inline: 'center'
    });
  }

  _onNext() {
    if (this._isExpanded) {
      this._next();
      return;
    }
    if (this._nextButton?.ariaDisabled === 'true') return;
    // Stop autoplay cascade when user manually scrolls
    this._stopAutoplayCascade();
    scrollPlaylist(this._playerContainer, this._players, 'right');
  }

  _onPrevious() {
    if (this._isExpanded) {
      this._prev();
      return;
    }
    if (this._prevButton?.ariaDisabled === 'true') return;
    // Stop autoplay cascade when user manually scrolls
    this._stopAutoplayCascade();
    scrollPlaylist(this._playerContainer, this._players, 'left');
  }

  _onScroll() {
    this._updateRowNavigationState();
    if (!this._isExpanded) return;

    if (this._scrollDirection === 'vertical') {
      // TODO: Exclude players that are clearly out of view to optimize performance
      this._players.forEach((player) => {
        if (!this._isExpanded) return;
        if (player !== this._focusedPlayer) {
          const visibleArea = player.getVisibleArea();
          if (visibleArea > 0.8) {
            // If a player is more than N% visible let it become the focused player.
            this._setFocusedPlayer(player);
          } else if (visibleArea > 0.2) {
            // If a player is more than M% visible, but not enough to become focused,
            // let's fade it in proportionally to how much is visible.
            player.parentElement.style.opacity = visibleArea;
          }
        }
      });
    }
    else if (this._scrollDirection === 'horizontal') {
      // Always focus on the player that is in the center
      this._players.forEach((player) => {
        if (!this._isExpanded) return;
        const positionRelativeToCenter = player.positionRelativeToCenter();
        if (positionRelativeToCenter === HORIZONTAL_POSITION.CENTER && player !== this._focusedPlayer) {
          this._setFocusedPlayer(player);
        }
      });
    }
  }

  _emit(eventName, data) {
    const event = new window.CustomEvent(eventName, { detail: data });
    this.dispatchEvent(event);
  }

  _printError(message, context) {
    console.error(message, context);
    if (debugMode) {
      emitDebugMessage(this._debugId, 'error', {
        message,
        context,
      });
    }
  }

  _printInfo(message, context) {
    if (process.env.NODE_ENV === 'development') console.info(message, context);
    if (debugMode) {
      emitDebugMessage(this._debugId, 'info', {
        message,
        context,
      });
    }
  }

  hideCloseButton() {
    this._wrapper?.classList?.add('hide-close-button');
  }

  showCloseButton() {
    this._wrapper?.classList?.remove('hide-close-button');
  }

  _saveFocus() {
    this._savedFocus = getDeepActiveElement();
  }

  _restoreFocus({ scroll = true } = {}) {
    if (this._savedFocus) {
      try {
        this._savedFocus.focus({ preventScroll: !scroll });
        setTimeout(() => {
          if (scroll) {
            this._savedFocus.scrollIntoView({
              block: 'nearest',
              inline: 'center',
            });
          }
          this._savedFocus = null;
        }, 30);
      } catch (err) {
        this._printError('Failed to restore focus', { err });
      }
    }
  }

  _applyCustomizations() {
    if (!this._customizations) return;

    const closeButton = this.shadow.querySelector('.close-button');
    if (closeButton) {
      closeButton.ariaLabel = this._customizations.generic?.closeAriaLabel || 'Close';
    }

    const fabPreviewCloseButton = this.shadow.querySelector('.fab-preview-close-button');
    if (fabPreviewCloseButton) {
      fabPreviewCloseButton.ariaLabel = this._customizations.generic?.closeAriaLabel || 'Close';
    }

    const prevButton = this.shadow.querySelector('.prev-button');
    if (prevButton) {
      prevButton.ariaLabel = this._customizations.generic?.prevAriaLabel || 'Previous';
    }
    const nextButton = this.shadow.querySelector('.next-button');
    if (nextButton) {
      nextButton.ariaLabel = this._customizations.generic?.nextAriaLabel || 'Next';
    }

    const loadMoreButton = this.shadow.querySelector('.load-more-button');
    if (loadMoreButton) {
      loadMoreButton.textContent = this._customizations.generic?.loadMoreLabel ?? 'Load More';
    }
  }

  applyPlayerConfig(config) {
    this._externallySetPlayerConfig = config || {};
    this._remotePlayerConfig = this._remotePlayerConfig || {};

    let requiresSetup = false;
    let requiresPlayerSettingsUpdate = false;

    Object.entries(this._externallySetPlayerConfig).forEach(([key, value]) => {
      if (this._remotePlayerConfig[key] !== value) {
        switch (key) {
          case 'playerFit':
          case 'zoomOnHover':
          case 'playerJustify':
          case 'thumbnailSize':
          case 'cornerRadius':
          case 'playerWidth':
          case 'playerHeight':
          case 'playerOverlayFontFamily':
          case 'playerOverlayFontWeight':
          case 'playerOverlayTitleFontSize':
          case 'playerOverlayTitleLineHeight':
          case 'playerProductCardPadding':
          case 'playerProductCardBackgroundColor':
          case 'playerProductCardTitleWrap':
          case 'playerPlaceholderColor':
          case 'playlistGap':
          case 'playlistCarouselCornerRadius':
          case 'playlistBackgroundColor':
          case 'showVerticalNavigationButtons':
          case 'showRowNavigationButtons':
            // These require updating just stylesheet
            break;
          case 'title':
          case 'productCardMode':
          case 'overlayTextWrap':
          case 'showScrollbars':
            requiresPlayerSettingsUpdate = true;
            break;
          default:
            requiresSetup = true;
            break;
        }
      }
    });

    const changedProps = Object.entries(this._externallySetPlayerConfig).map(([key, value]) => {
      if (this._remotePlayerConfig[key] !== value) {
        return key;
      } else {
        return null;
      }
    }).filter(Boolean);

    // A redundant re-apply (e.g. the preview's throttled trailing re-send) carries
    // no changes — bail out so we neither re-render nor, worse, contract the
    // expanded player for nothing.
    if (changedProps.length === 0) return;

    // If only visual, easily-updatable props changed, we can skip the contract
    // step even while expanded — smoother when updating things like background
    // color or corner radius.
    const maySkipContract = changedProps.length === 1 &&
      [
        'playlistBackgroundColor',
        'playlistCarouselCornerRadius',
        'showScrollbars',
        'showVerticalNavigationButtons',
        'showRowNavigationButtons',
      ].includes(changedProps[0]);

    if (this._isExpanded && !maySkipContract) {
      // If we're expanded, we need to contract first before applying new config
      // Let's re-setup the playlist to ensure a clean state
      this._contract();
      requiresSetup = true;
    }

    const bodyBamPlaylist = document.body.querySelector(':scope > bam-playlist');
    if (bodyBamPlaylist?._attr?.mode === 'fab' && bodyBamPlaylist?._isExpanded) {
      // If we're expanded in FAB mode, we need to contract first before applying new config
      // Let's re-setup the playlist to ensure a clean state
      bodyBamPlaylist._contract();
      requiresSetup = true;
    }

    if (requiresSetup) {
      // Remove any existing bam-playlist in the body if switching out of fab mode
      if (bodyBamPlaylist?._attr?.mode === 'fab' && this._externallySetPlayerConfig.mode !== 'fab') {
        document.body.removeChild(bodyBamPlaylist);
      }

      this.dataset.status = 'loading';
      this._setup();
      return;
    }

    this._remotePlayerConfig = {
      ...this._remotePlayerConfig,
      ...this._externallySetPlayerConfig,
    };

    if (requiresPlayerSettingsUpdate) {
      this._updatePlayersSettingsFromAttributes();
      this._applyPlayerSettingsUpdate();
    }

    this._updateStylesheet();
  }

  applyAppearanceConfig(config) {
    this._externallySetAppearanceConfig = config.appearanceGroup || {};

    const bodyBamPlaylist = document.body.querySelector(':scope > bam-playlist');
    if (bodyBamPlaylist?._attr?.mode === 'fab' && this._externallySetPlayerConfig.mode !== 'fab') {
      document.body.removeChild(bodyBamPlaylist);
    }

    this.dataset.status = 'loading';

    this._setup();
  }

  applyPlatformConfig(config) {
    if (!config) return;

    const { id } = config || {};

    // This update changes some styling decisions based on whether we're on desktop or not
    // eg if the player should go fullscreen when expanding or not
    if (id?.includes('desktop')) {
      this._isDesktop = true;
    } else {
      this._isDesktop = false;
    }
    this._updateStylesheet();
  }

  setPreviewOrgId(orgId) {
    this._previewOrgId = orgId;
  }

  _exportContext() {
    const attr = this._attr;
    attr.videoIds = this._players.map((player) => player._attr.videoId);
    const startVideoId = this._focusedPlayer ? this._focusedPlayer._attr.videoId : null;
    if (startVideoId) attr.startVideoId = startVideoId;
    const props = {};
    
    [
      '_remotePlayerConfig',
      '_themeId',
      '_distributionPageId',
      '_distributionContainerId',
      '_pageSize',
      '_total',
      '_page',
      '_hasNextPage',
    ].forEach((propName) => {
      props[propName] = this[propName];
    });
    return {
      attr,
      props,
    };
  }

  _resetWithImportedContext(context) {
    const {
      attr,
      props,
    } = context;

    this._attrOverrides = attr;
    Object.entries(props).forEach(([key, value]) => {
      this[key] = value;
    });
    delete this._lastSetupId;
    this._setup();
  }
}
